// @ts-nocheck
/*
 * Port of views/dashboard/manage-agents/groupSettings.js (3846 lines).
 * BYTE-IDENTICAL logic.
 *
 * The single page for a call group's IVR-adjacent config:
 *   - Inbound number allocation (multi-select CLI + one dedicated)
 *   - Add/remove members; each member's priority (0-10) is edited from that member's edit action
 *   - Group time schedule + per-member time schedule + edit-member schedule
 *   - Call strategy picker
 *   - Agent whisper prompt + language
 *   - No-agent-available prompt + language
 *   - Inbound + outbound disposition forms
 *   - Call script attach/change/remove
 *   - Sticky (Multi-IVR) / sticky-member (off, or hard_sticky / soft_sticky)
 *   - Queue wait (shows max wait time) vs hangup (shows no-agent prompt); ringing timeout last
 *   - Music-on-Hold picker (only own approved MOH)
 *   - Customer disclaimer toggle + prompt + language
 *   - Voicemail toggle (only if call center allows child voicemail management)
 *
 * Swaps from v1:
 *  - PrimeReact MultiSelect (inbound numbers) â†’ react-select isMulti
 *  - v1 LottieAnimation + bespoke bootstrap "showToast" modal â†’ react-toastify
 *    toast.success (avoids pulling lottie-web / lottie-react deps for a
 *    minor confirmation UX); local showToast() is redefined to call toast.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
Form.Text = ({ muted, className, children, ...props }: any) => (
  <small className={_cx('form-text', muted && 'text-muted', className)} {...props}>
    {children}
  </small>
);
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
const Tooltip: any = ({ children, id: _id, ...props }: any) => <span {...props}>{children}</span>;
const OverlayTrigger: any = ({ children, overlay, placement: _p, trigger: _t }: any) => {
  let title = '';
  if (overlay?.props?.children) {
    title =
      typeof overlay.props.children === 'string'
        ? overlay.props.children
        : Array.isArray(overlay.props.children)
          ? overlay.props.children.filter((c: any) => typeof c === 'string').join(' ')
          : '';
  }
  return React.cloneElement(children, { title });
};
import { getApiCall } from '@/service/httpCall';
import { fetchAndSetFlashMessage } from '@/service/fetchFlashMessage';
import {
  API_AGENTS_LIST,
  API_SINGLE_AGENT_GROUP,
  API_AGENT_GROUP_ASSIGNED_AGENTS,
  API_PROMPT_ACTIVE_LIST,
  API_CALL_STRATEGIES_MASTER_LIST,
  API_AGENT_GROUP_AVAILABLE_INBOUND_NUMBERS,
  API_AGENT_GROUP_CLI_ALLOCATIONS_SYNC,
  API_CALL_SCRIPT_LIST,
  API_MOH_ACTIVE,
  API_AGENT_GROUP_APPROVED_PROMPTS,
  API_DISPOSITION_LIST,
  API_CALL_CENTRE_PROFILE,
} from '@/service/url';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { postApiCall } from '@/service/postApiCall';
import ReactSelect from 'react-select';
import { SelectAllToggle } from '@/components/ui/select-all-toggle';
import { FiInfo, FiEdit, FiTrash2 } from 'react-icons/fi';
import {
  Users,
  Clock,
  GitBranch,
  ClipboardList,
  Target,
  FileText,
  ChevronRight,
  Speech,
} from 'lucide-react';
import { MODULE_ACL, FLASH_MESSAGE_ACTION_OBJECT, USER_ROLES, WEEK_DAYS, DEFAULT_SCHEDULE_START, DEFAULT_SCHEDULE_END } from '@/utilities/constant';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Storage } from '@/utilities/setting';
import { canEditCallGroupOption, getCallGroupAclEntry, getUserModuleOperations } from '@/utilities/callGroupAcl';
import { formMatchesCallDirection, formatDispositionCallDirectionLabel } from '@/utilities/dispositionCallDirection';
import { CalendarTimeInput } from '@/components/ui/calendar-inputs';
import {
  canManageChildVoicemail,
  getAssignedCallCentreId,
  readVoicemailEnabled,
} from '@/utilities/callCentreVoicemailAcl';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';

function isCallGroupRoutingType(routingType) {
  const normalized = String(routingType || '')
    .toUpperCase()
    .replace(/[\s_-]+/g, '');
  return normalized === 'QUEUE' || normalized === 'CALLGROUP';
}

function isInboundNumberSelectableForGroup() {
  return true;
}

function isNumberAssignedToCallGroup(n, groupId) {
  const gid = String(groupId);
  if (n.cli_target_type === 'QUEUE' && String(n.cli_target_id) === gid) return true;
  return n.cli_target_type == null && isCallGroupRoutingType(n.routing_type) && String(n.routing_id) === gid;
}

// Most outbound CLIs one call group can be given ("Outbound caller IDs").
const MAX_GROUP_CLIS = 5;
const QUEUE_WAIT = '1';
const QUEUE_HANGUP = '0';
const RING_TIMEOUT_MIN = 5;
const RING_TIMEOUT_MAX = 60;
const RING_TIMEOUT_DEFAULT = 30;
const MAX_WAIT_MIN = 20;
const MAX_WAIT_MAX = 300;
const MAX_WAIT_DEFAULT = 180;
const STICKY_HARD = 'hard_sticky';
const STICKY_SOFT = 'soft_sticky';

function parseStickyMember(value) {
  if (value === STICKY_HARD || value === 'advance') {
    return { enabled: true, mode: STICKY_HARD };
  }
  if (value === STICKY_SOFT || value === 'normal') {
    return { enabled: true, mode: STICKY_SOFT };
  }
  return { enabled: false, mode: STICKY_SOFT };
}

function parseBoundedInt(raw, fallback, min, max) {
  const value = Number(raw);
  if (!Number.isInteger(value)) return fallback;
  if (value < min || value > max) return fallback;
  return value;
}

function getRingTimeoutError(value) {
  if (value === '' || value === null || value === undefined) {
    return `Enter a value between ${RING_TIMEOUT_MIN} and ${RING_TIMEOUT_MAX} seconds.`;
  }
  const n = Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return 'Enter a whole number of seconds.';
  if (n < RING_TIMEOUT_MIN || n > RING_TIMEOUT_MAX) {
    return `Must be between ${RING_TIMEOUT_MIN} and ${RING_TIMEOUT_MAX} seconds.`;
  }
  return '';
}

function getMaxWaitError(value, ringValue) {
  if (value === '' || value === null || value === undefined) {
    return `Enter a value between ${MAX_WAIT_MIN} and ${MAX_WAIT_MAX} seconds.`;
  }
  const n = Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return 'Enter a whole number of seconds.';
  if (n < MAX_WAIT_MIN || n > MAX_WAIT_MAX) {
    return `Must be between ${MAX_WAIT_MIN} and ${MAX_WAIT_MAX} seconds.`;
  }
  const ring = Number(ringValue);
  if (Number.isInteger(ring) && n < ring) {
    return 'Max wait time cannot be less than agent ringing timeout.';
  }
  return '';
}

const GroupSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeGroupId } = useParams();
  const groupId = (location.state as any)?.groupId ?? routeGroupId;

  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [agentGroup, setAgentGroup] = useState<any>({});
  const [showTimeScheduleModal, setShowTimeScheduleModal] = useState(false);
  const [showWhisperModal, setShowWhisperModal] = useState(false);
  const [showNoAgentPromptModal, setShowNoAgentPromptModal] = useState(false);
  const [agents, setAgents] = useState<any[]>([]);
  const [showCallStrategyModal, setShowCallStrategyModal] = useState(false);

  const [selectedAgentIds, setSelectedAgentIds] = useState<any[]>([]);
  const [startTime, setStartTime] = useState(DEFAULT_SCHEDULE_START);
  const [endTime, setEndTime] = useState(DEFAULT_SCHEDULE_END);

  const [weekDays, setWeekDays] = useState<string[]>([
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ]);
  const [memberWeekDays, setmemberWeekDays] = useState<string[]>([]);
  const [selectedStrategy, setSelectedStrategy] = useState('ROUNDROBIN RINGING');
  const [sticky, setSticky] = useState(false);
  const [stickyMemberEnabled, setStickyMemberEnabled] = useState(false);
  const [stickyMemberMode, setStickyMemberMode] = useState(STICKY_SOFT);
  const [shouldWaitOrHangup, setShouldWaitOrHangup] = useState(QUEUE_WAIT);
  const [allAgentRingingTimeout, setAllAgentRingingTimeout] = useState(RING_TIMEOUT_DEFAULT);
  const [maxWaitTime, setMaxWaitTime] = useState(MAX_WAIT_DEFAULT);
  const [voicemailEnabled, setVoicemailEnabled] = useState(false);
  const [callCentreProfile, setCallCentreProfile] = useState<any>(null);
  const [time, setTime] = useState(false);
  const [callStrategy, setCallStratergy] = useState<any[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const ignoreEditModalCloseRef = useRef(false);
  const [assignedAgents, setAssignedAgents] = useState<any[]>([]);
  const [availableAgents, setAvailableAgents] = useState<any[]>([]);
  const [editData, setEditData] = useState<any>({
    id: '',
    startTime: '',
    endTime: '',
    weekDays: [],
    agent_id: '',
  });
  const [timeScheduledDate, setTimeScheduledDate] = useState<any>({
    startTime: DEFAULT_SCHEDULE_START,
    endTime: DEFAULT_SCHEDULE_END,
    weekDays: [...WEEK_DAYS],
  });
  const [timeScheduleWholeDay, setTimeScheduleWholeDay] = useState(true);
  const [editDataWholeDay, setEditDataWholeDay] = useState(false);
  const [showMusicOnHold, setShowMusicOnHold] = useState(false);
  const [priorityError, setPriorityError] = useState('');
  const [wholeDay, setWholeDay] = useState(true);
  const [groupScheduledId, setGroupScheduledId] = useState('');
  const [selectedWhisper, setSelectedWhisper] = useState<any>('');
  const [selectedWhisperLanguage, setSelectedWhisperLanguage] = useState('');
  const [selectedNoAgentPromptId, setSelectedNoAgentPromptId] = useState('');
  const [selectedNoAgentPromptLanguage, setSelectedNoAgentPromptLanguage] = useState('');
  const [customerDisclaimerEnabled, setCustomerDisclaimerEnabled] = useState(false);
  const [selectedCustomerDisclaimerPromptId, setSelectedCustomerDisclaimerPromptId] = useState('');
  const [selectedCustomerDisclaimerLanguage, setSelectedCustomerDisclaimerLanguage] = useState('');
  const [showCustomerDisclaimerModal, setShowCustomerDisclaimerModal] = useState(false);
  const [savingCustomerDisclaimer, setSavingCustomerDisclaimer] = useState(false);
  const [selectedMusicHold, setSelectedMusicHold] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isGroupScheduled, setIsGroupScheduled] = useState(false);
  const [notes, setNotes] = useState<any[]>([]);
  const [savingMain, setSavingMain] = useState(false);
  const [savingMembers, setSavingMembers] = useState(false);
  const [savingStrategy, setSavingStrategy] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [savingWhisper, setSavingWhisper] = useState(false);
  const [savingNoAgentPrompt, setSavingNoAgentPrompt] = useState(false);
  const [savingAgentSchedule, setSavingAgentSchedule] = useState(false);
  const [priorityDrafts, setPriorityDrafts] = useState<any>({});
  const [priorityRowErrors, setPriorityRowErrors] = useState<any>({});
  const [savingPriorities, setSavingPriorities] = useState(false);

  const [availableInboundNumbers, setAvailableInboundNumbers] = useState<any[]>([]);
  const [selectedInboundNumberIds, setSelectedInboundNumberIds] = useState<number[]>([]);
  const [dedicatedInboundNumberId, setDedicatedInboundNumberId] = useState<any>(null);
  const [loadingAvailableNumbers, setLoadingAvailableNumbers] = useState(false);
  const [savingInboundRouting, setSavingInboundRouting] = useState(false);
  const [agentPriorities, setAgentPriorities] = useState<any>({});
  const [addMemberPriorityErrors, setAddMemberPriorityErrors] = useState<any>({});

  const [showCallScriptModal, setShowCallScriptModal] = useState(false);
  const [callScripts, setCallScripts] = useState<any[]>([]);
  const [selectedCallScriptId, setSelectedCallScriptId] = useState('');
  const [pendingCallScriptId, setPendingCallScriptId] = useState('');
  const [savingCallScript, setSavingCallScript] = useState(false);
  const [loadingCallScripts, setLoadingCallScripts] = useState(false);
  const [timeScheduleWholeWeek, setTimeScheduleWholeWeek] = useState(true);
  const [memberWholeWeek, setMemberWholeWeek] = useState(true);

  const [showDispositionModal, setShowDispositionModal] = useState(false);
  const [dispositionForms, setDispositionForms] = useState<any[]>([]);
  const [selectedDispositionId, setSelectedDispositionId] = useState('');
  const [pendingDispositionId, setPendingDispositionId] = useState('');
  const [savingDisposition, setSavingDisposition] = useState(false);
  const [loadingDispositions, setLoadingDispositions] = useState(false);

  const [dispositionModalFlow, setDispositionModalFlow] = useState<any>(null);
  const [selectedDispositionIdInbound, setSelectedDispositionIdInbound] = useState('');
  const [selectedDispositionIdOutbound, setSelectedDispositionIdOutbound] = useState('');

  const storedUser = (() => {
    try {
      const raw = Storage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.error('Failed to parse user from storage', e);
      return null;
    }
  })();
  const loggedInUserId = storedUser?.id;

  const userModuleOps = useMemo(() => {
    try {
      const raw = Storage.getItem('user');
      const user = raw ? JSON.parse(raw) : null;
      return getUserModuleOperations(user);
    } catch {
      return {};
    }
  }, []);

  const callGroupAclContext = useMemo(
    () => ({ userRole: storedUser?.role, isSuperAdmin: storedUser?.role === USER_ROLES.SUPERADMIN }),
    [storedUser?.role],
  );

  const canMemberManagement = useMemo(() => canEditCallGroupOption(userModuleOps, 'member_management', callGroupAclContext), [userModuleOps, callGroupAclContext]);
  const canCallStrategy = useMemo(() => canEditCallGroupOption(userModuleOps, 'call_strategy', callGroupAclContext), [userModuleOps, callGroupAclContext]);
  const canAgentWhisper = useMemo(() => canEditCallGroupOption(userModuleOps, 'agent_whisper', callGroupAclContext), [userModuleOps, callGroupAclContext]);
  const canInboundDisposition = useMemo(() => canEditCallGroupOption(userModuleOps, 'inbound_disposition', callGroupAclContext), [userModuleOps, callGroupAclContext]);
  const canOutboundDisposition = useMemo(() => canEditCallGroupOption(userModuleOps, 'outbound_disposition', callGroupAclContext), [userModuleOps, callGroupAclContext]);
  const canCallScript = useMemo(() => canEditCallGroupOption(userModuleOps, 'call_script', callGroupAclContext), [userModuleOps, callGroupAclContext]);
  const canMultiIvrSticky = useMemo(() => canEditCallGroupOption(userModuleOps, 'multi_ivr_sticky', callGroupAclContext), [userModuleOps, callGroupAclContext]);
  const canWaitingAudio = useMemo(() => canEditCallGroupOption(userModuleOps, 'waiting_audio', callGroupAclContext), [userModuleOps, callGroupAclContext]);
  const canSequentialForwarding = useMemo(() => canEditCallGroupOption(userModuleOps, 'sequential_call_forwarding', callGroupAclContext), [userModuleOps, callGroupAclContext]);
  const canGroupTimeSchedule = useMemo(() => canEditCallGroupOption(userModuleOps, 'group_time_schedule', callGroupAclContext), [userModuleOps, callGroupAclContext]);
  const canNoAgentPrompt = useMemo(() => canEditCallGroupOption(userModuleOps, 'no_agent_prompt', callGroupAclContext), [userModuleOps, callGroupAclContext]);
  const canInboundNumbers = useMemo(() => canEditCallGroupOption(userModuleOps, 'inbound_numbers', callGroupAclContext), [userModuleOps, callGroupAclContext]);
  const canVoicemailEnabled = useMemo(() => canManageChildVoicemail(callCentreProfile), [callCentreProfile]);
  const canEditCallGroup = useMemo(() => {
    if (callGroupAclContext.isSuperAdmin) return true;
    const entry = getCallGroupAclEntry(userModuleOps);
    return entry?.edit === true;
  }, [userModuleOps, callGroupAclContext]);
  const canSaveMainSettings =
    canMultiIvrSticky ||
    canWaitingAudio ||
    canSequentialForwarding ||
    canVoicemailEnabled ||
    canEditCallGroup ||
    canNoAgentPrompt;

  useEffect(() => {
    getApiCall(`${API_PROMPT_ACTIVE_LIST}/${loggedInUserId}`).then(onPromptsSuccess).catch(onError);
  }, []);

  function onPromptsSuccess(resp) {
    const list = Array.isArray(resp) ? resp : [];
    setAudio(list.filter(isSelectablePrompt));
  }

  function onError(err) {
    console.log(err);
  }

  const [audio, setAudio] = useState<any[]>([]);
  const [noAgentPromptOptions, setNoAgentPromptOptions] = useState<any[]>([]);
  const [approvedPromptOptions, setApprovedPromptOptions] = useState<any[]>([]);
  const [loadingNoAgentPromptOptions, setLoadingNoAgentPromptOptions] = useState(false);

  const approvedLanguageEntries = (prompt) =>
    (Array.isArray(prompt?.prompt_url) ? prompt.prompt_url : []).filter(
      (entry) => Number(entry?.status) === 1 && entry?.language,
    );

  const isSelectablePrompt = (prompt) => Number(prompt?.prompt_status) === 1 && approvedLanguageEntries(prompt).length > 0;

  const getApprovedPromptLanguages = (promptId, list) => {
    const prompt = (list || []).find((p) => String(p.id) === String(promptId));
    return approvedLanguageEntries(prompt);
  };

  const noAgentPromptLanguages = getApprovedPromptLanguages(selectedNoAgentPromptId, noAgentPromptOptions);
  const whisperPromptLanguages = getApprovedPromptLanguages(selectedWhisper, audio);
  const customerDisclaimerLanguages = getApprovedPromptLanguages(selectedCustomerDisclaimerPromptId, approvedPromptOptions);

  const [mohOptions, setMohOptions] = useState<any[]>([]);
  const [mohLoading, setMohLoading] = useState(false);
  const [mohError, setMohError] = useState('');

  const fetchMohOptions = () => {
    setMohLoading(true);
    setMohError('');
    getApiCall(API_MOH_ACTIVE)
      .then((resp: any) => {
        const arr = Array.isArray(resp) ? resp : resp?.data || [];
        const filtered = arr.filter(
          (m) => Number(m.moh_status) === 1 && !m.is_deleted && Number(m.created_by) === Number(loggedInUserId),
        );
        setMohOptions(filtered);
      })
      .catch((err) => {
        setMohError(typeof err === 'string' ? err : err?.message || 'Failed to load MOH list');
        setMohOptions([]);
      })
      .finally(() => setMohLoading(false));
  };

  useEffect(() => {
    if (loggedInUserId) fetchMohOptions();
  }, [loggedInUserId]);

  const fetchNoAgentPromptOptions = async () => {
    if (!groupId) return;
    try {
      setLoadingNoAgentPromptOptions(true);
      const resp: any = await getApiCall(API_AGENT_GROUP_APPROVED_PROMPTS.replace(':id', groupId));
      const options = Array.isArray(resp) ? resp : [];
      const selectable = options.filter(isSelectablePrompt);
      const noAgentPrompts = selectable.filter((prompt) => prompt.prompt_category);
      setNoAgentPromptOptions(noAgentPrompts);
      setApprovedPromptOptions(selectable);
    } catch (err) {
      console.error('Error fetching no-agent prompt options:', err);
      setNoAgentPromptOptions([]);
      setApprovedPromptOptions([]);
    } finally {
      setLoadingNoAgentPromptOptions(false);
    }
  };

  useEffect(() => {
    fetchNoAgentPromptOptions();
  }, [groupId]);

  const handleWholeDayChange = (e) => {
    const isChecked = e.target.checked;
    setWholeDay(isChecked);
    if (isChecked) {
      setStartTime(DEFAULT_SCHEDULE_START);
      setEndTime(DEFAULT_SCHEDULE_END);
    } else {
      setStartTime('');
      setEndTime('');
    }
  };

  const toMinutes = (timeValue) => {
    if (!timeValue || !String(timeValue).includes(':')) return NaN;
    const [hours, minutes] = String(timeValue).split(':').map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return NaN;
    return hours * 60 + minutes;
  };

  function convertTo12Hour(time24) {
    if (!time24) return '';
    let [hours, minutes] = time24.split(':');
    hours = parseInt(hours, 10);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours === 0 ? 12 : hours;
    return `${hours}:${minutes}${ampm}`;
  }

  const hasSelectedWholeWeek = (selectedDays: any = [], availableDays: any = []) => {
    if (!Array.isArray(selectedDays) || !Array.isArray(availableDays) || availableDays.length === 0) return false;
    return availableDays.every((day) => selectedDays.includes(day));
  };

  const getWholeWeekDays = (availableDays: any = []) => (Array.isArray(availableDays) ? [...availableDays] : []);

  const handleStartTimeChange = (e) => {
    const value = e.target.value;
    if (value < timeScheduledDate.startTime || value > timeScheduledDate.endTime) {
      toast.error(
        `Start time should be within allowed Range (${convertTo12Hour(timeScheduledDate.startTime)} to ${convertTo12Hour(timeScheduledDate.endTime)})`,
      );
      return;
    }
    if (!wholeDay) setStartTime(e.target.value);
  };

  const handleEndTimeChange = (e) => {
    const value = e.target.value;
    if (value < timeScheduledDate.startTime || value > timeScheduledDate.endTime) {
      toast.error(
        `End time should be within allowed range (${convertTo12Hour(startTime)} to ${convertTo12Hour(timeScheduledDate.endTime)})`,
      );
      return;
    }
    if (value < startTime) {
      toast.error('End time cannot be less than start time');
      return;
    }
    if (!wholeDay) setEndTime(e.target.value);
  };

  // v1 rendered showToast(msg) via a LottieAnimation modal for 3s â€” swapped for
  // react-toastify's toast.success (no lottie deps in v2). Confirmation UX
  // preserved via the toast; only the animation is dropped.
  const showToast = (message) => {
    toast.success(message);
  };

  const handleAddMembersClick = () => {
    setWholeDay(true);
    setStartTime(DEFAULT_SCHEDULE_START);
    setEndTime(DEFAULT_SCHEDULE_END);
    const days =
      Array.isArray(timeScheduledDate?.weekDays) && timeScheduledDate.weekDays.length
        ? [...timeScheduledDate.weekDays]
        : [...WEEK_DAYS];
    setmemberWeekDays(days);
    setMemberWholeWeek(true);
    setShowAddMembersModal(true);
  };
  const handleModalClose = () => {
    setMemberWholeWeek(true);
    setmemberWeekDays([]);
    setWholeDay(true);
    setStartTime(DEFAULT_SCHEDULE_START);
    setEndTime(DEFAULT_SCHEDULE_END);
    setShowAddMembersModal(false);
  };

  const handleTimeScheduleClick = () => {
    const prev = timeScheduledDate || {};
    const hasDays = Array.isArray(prev.weekDays) && prev.weekDays.length > 0;
    const next = {
      startTime: prev.startTime || DEFAULT_SCHEDULE_START,
      endTime: prev.endTime || DEFAULT_SCHEDULE_END,
      weekDays: hasDays ? prev.weekDays : [...WEEK_DAYS],
    };
    setTimeScheduledDate(next);
    setTimeScheduleWholeDay(
      next.startTime === DEFAULT_SCHEDULE_START && next.endTime === DEFAULT_SCHEDULE_END,
    );
    setTimeScheduleWholeWeek(WEEK_DAYS.every((d) => next.weekDays.includes(d)));
    setShowTimeScheduleModal(true);
  };
  const handleTimeScheduleClose = () => {
    setTime(true);
    setShowTimeScheduleModal(false);
  };
  const handleWhisperClick = () => setShowWhisperModal(true);
  const handleWhisperClose = () => setShowWhisperModal(false);
  const handleCallStrategyClick = () => setShowCallStrategyModal(true);
  const handleCallStrategyCloseall = () => setShowCallStrategyModal(false);

  const handleCallStrategyClose = async () => {
    if (savingStrategy) return;
    const finalPayload = { strategy: selectedStrategy, type: 'strategy' };
    try {
      setSavingStrategy(true);
      const response = await postApiCall(API_SINGLE_AGENT_GROUP.replace(':id', groupId), finalPayload);
      if (response) {
        setShowCallStrategyModal(false);
        showToast('Call Stratergy Updated');
        setShowAddMembersModal(false);
      }
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Failed to update call strategy');
    } finally {
      setSavingStrategy(false);
    }
  };

  const handleSaveTimeSchedule = () => {
    if (savingSchedule) return;
    if (timeScheduledDate.weekDays == 0 || !timeScheduledDate.weekDays || timeScheduledDate.weekDays.length === 0) {
      toast.error('Please select atleast one week day');
      return;
    }

    if (!timeScheduleWholeDay) {
      if (!timeScheduledDate.startTime || timeScheduledDate.startTime.trim() === '' || timeScheduledDate.startTime === '--:-- --') {
        toast.error('Please select a start time');
        return;
      }
      if (!timeScheduledDate.endTime || timeScheduledDate.endTime.trim() === '' || timeScheduledDate.endTime === '--:-- --') {
        toast.error('Please select an end time');
        return;
      }
      const startMins = toMinutes(timeScheduledDate.startTime);
      const endMins = toMinutes(timeScheduledDate.endTime);
      if (!Number.isFinite(startMins) || !Number.isFinite(endMins)) {
        toast.error('Invalid time selected');
        return;
      }
      if (timeScheduledDate.weekDays.length === 1 && endMins <= startMins) {
        toast.error('For a single selected day, end time should be greater than start time');
        return;
      }
    }

    const payload = {
      ...timeScheduledDate,
      type: 'time_schedule',
      group_schedule_id: groupScheduledId,
      startTime: timeScheduleWholeDay ? '00:00' : timeScheduledDate.startTime,
      endTime: timeScheduleWholeDay ? '23:59' : timeScheduledDate.endTime,
    };

    setSavingSchedule(true);
    postApiCall(`/v2/agent-group/${groupId}`, payload)
      .then((response) => {
        if (response) {
          setShowTimeScheduleModal(false);
          showToast('Time Schedule Updated');
          setIsGroupScheduled(true);
        }
      })
      .catch((err) => {
        toast.error(friendlyApiError(err) || 'Failed to update time schedule');
      })
      .finally(() => {
        setSavingSchedule(false);
      });
  };

  const getAgentGroupData = async () => {
    if (!groupId) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const response: any = await getApiCall(API_SINGLE_AGENT_GROUP.replace(':id', groupId));
      if (response) {
        setTimeScheduledDate({
          startTime: response?.group_schedule?.start_time || DEFAULT_SCHEDULE_START,
          endTime: response?.group_schedule?.end_time || DEFAULT_SCHEDULE_END,
          weekDays:
            Array.isArray(response?.group_schedule?.week_days) &&
            response.group_schedule.week_days.length
              ? response.group_schedule.week_days
              : [...WEEK_DAYS],
        });
        if (response?.group_schedule?.start_time) setIsGroupScheduled(true);
        const loadedStart = response?.group_schedule?.start_time || DEFAULT_SCHEDULE_START;
        const loadedEnd = response?.group_schedule?.end_time || DEFAULT_SCHEDULE_END;
        setTimeScheduleWholeDay(
          loadedStart === DEFAULT_SCHEDULE_START && loadedEnd === DEFAULT_SCHEDULE_END,
        );
        const loadedDays = Array.isArray(response?.group_schedule?.week_days)
          ? response.group_schedule.week_days
          : [];
        setTimeScheduleWholeWeek(
          loadedDays.length === 0 || WEEK_DAYS.every((d) => loadedDays.includes(d)),
        );
        setSelectedStrategy(response?.strategy || 'ROUNDROBIN RINGING');
        setSelectedWhisper(response?.whisper_id || '');
        setSelectedWhisperLanguage(response?.whisper_language || '');
        setSelectedNoAgentPromptId(
          response?.no_agent_available_prompt_id != null && response?.no_agent_available_prompt_id !== ''
            ? String(response.no_agent_available_prompt_id)
            : '',
        );
        setSelectedNoAgentPromptLanguage(response?.no_agent_available_prompt_language || '');
        setCustomerDisclaimerEnabled(response?.customer_disclaimer_enabled === true);
        setSelectedCustomerDisclaimerPromptId(
          response?.customer_disclaimer_prompt_id != null && response?.customer_disclaimer_prompt_id !== ''
            ? String(response.customer_disclaimer_prompt_id)
            : '',
        );
        setSelectedCustomerDisclaimerLanguage(response?.customer_disclaimer_language || '');
        setSticky(response?.sticky === true || response?.sticky === 1);
        setSelectedMusicHold(response?.music_on_hold || null);
        const stickyParsed = parseStickyMember(response?.sticky_member);
        setStickyMemberEnabled(stickyParsed.enabled);
        setStickyMemberMode(stickyParsed.mode);
        setShouldWaitOrHangup(
          String(response?.should_wait_or_hangup) === QUEUE_HANGUP ? QUEUE_HANGUP : QUEUE_WAIT,
        );
        setAllAgentRingingTimeout(
          parseBoundedInt(
            response?.all_agent_ringing_timeout,
            RING_TIMEOUT_DEFAULT,
            RING_TIMEOUT_MIN,
            RING_TIMEOUT_MAX,
          ),
        );
        setMaxWaitTime(
          parseBoundedInt(response?.max_wait_time, MAX_WAIT_DEFAULT, MAX_WAIT_MIN, MAX_WAIT_MAX),
        );
        setVoicemailEnabled(readVoicemailEnabled(response));
        const ccId = response?.call_center_id ?? getAssignedCallCentreId(storedUser);
        if (ccId) {
          getApiCall(API_CALL_CENTRE_PROFILE.replace(':id', ccId))
            .then((profile) => setCallCentreProfile(profile))
            .catch(() => setCallCentreProfile(null));
        } else {
          setCallCentreProfile(null);
        }
        setSelectedCallScriptId(response?.script_id != null && response?.script_id !== '' ? String(response.script_id) : '');
        setSelectedDispositionId(response?.disposition_id != null && response?.disposition_id !== '' ? String(response.disposition_id) : '');
        setSelectedDispositionIdInbound(
          response?.disposition_id_inbound != null && response?.disposition_id_inbound !== ''
            ? String(response.disposition_id_inbound)
            : '',
        );
        setSelectedDispositionIdOutbound(
          response?.disposition_id_outbound != null && response?.disposition_id_outbound !== ''
            ? String(response.disposition_id_outbound)
            : '',
        );
        setAgentGroup(response);
        setGroupScheduledId(response.group_schedule_id);
        setIsDataLoaded(true);
      } else {
        toast.error('Failed to fetch assigned agents.');
        setIsDataLoaded(true);
      }
    } catch (error) {
      console.error('Error fetching assigned agents:', error);
      toast.error('Error fetching assigned agents.');
      setIsDataLoaded(true);
    } finally {
      setIsLoading(false);
    }
  };

  const memberWeekDayList = (rawDays) => {
    if (Array.isArray(rawDays) && rawDays.length) return rawDays;
    if (typeof rawDays === 'string' && rawDays.trim()) {
      try {
        const parsed = JSON.parse(rawDays);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      } catch {
        const parts = rawDays.split(',').map((day) => day.trim()).filter(Boolean);
        if (parts.length) return parts;
      }
    }
    return [...WEEK_DAYS];
  };

  const handleEditClick = (agent) => {
    const weekDaysList = memberWeekDayList(agent?.schedule?.week_days);
    const rawPriority = agent?.priority;
    setEditData({
      agent_id: agent?.agent_id,
      agent_name: agent?.agent_name || '',
      id: agent?.schedule?.id,
      startTime: agent?.schedule?.start_time || DEFAULT_SCHEDULE_START,
      endTime: agent?.schedule?.end_time || DEFAULT_SCHEDULE_END,
      weekDays: weekDaysList,
      priority:
        rawPriority === undefined || rawPriority === null || rawPriority === ''
          ? '10'
          : String(rawPriority),
    });
    if (
      !agent?.schedule?.start_time ||
      (agent?.schedule?.start_time === DEFAULT_SCHEDULE_START &&
        agent?.schedule?.end_time === DEFAULT_SCHEDULE_END)
    )
      setEditDataWholeDay(true);
    else setEditDataWholeDay(false);

    setPriorityError('');
    setShowEditModal(true);
  };

  const memberPriorityNumber = (agent) => {
    const raw = agent?.priority;
    if (raw === undefined || raw === null || raw === '') return 10;
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? 10 : parsed;
  };

  const openMemberEditor = (agent) => {
    ignoreEditModalCloseRef.current = true;
    handleEditClick(agent);
    window.setTimeout(() => {
      ignoreEditModalCloseRef.current = false;
    }, 500);
  };

  const fetchAgentData = async () => {
    if (!groupId) return;
    try {
      const [agentsResponse, assignedAgentsResponse] = await Promise.all([
        getApiCall(API_AGENTS_LIST),
        getApiCall(API_AGENT_GROUP_ASSIGNED_AGENTS.replace(':id', groupId)),
      ]);
      const agents: any = agentsResponse || [];
      const assignedAgents: any = assignedAgentsResponse || [];
      const assignedAgentIds = new Set(assignedAgents.map((assigned) => Number(assigned.agent_id)));
      const available = agents.filter((agent) => !assignedAgentIds.has(Number(agent.id)));
      setAvailableAgents(available);
      setAssignedAgents(assignedAgents);
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('Failed to fetch agent data.');
    }
  };

  const handleAddMembersSave = async () => {
    if (savingMembers) return;

    if (memberWeekDays == 0) {
      toast.error('Please select atleat one week day');
      return;
    }

    if (!wholeDay) {
      const startMins = toMinutes(startTime);
      const endMins = toMinutes(endTime);
      if (!Number.isFinite(startMins) || !Number.isFinite(endMins)) {
        toast.error('Invalid time selected');
        return;
      }
      if (memberWeekDays.length === 1 && endMins <= startMins) {
        toast.error('For a single selected day, end time should be greater than start time');
        return;
      }
    }
    const selectedAgents = availableAgents
      .filter((agent) => selectedAgentIds.includes(agent.id))
      .map((agent) => ({
        ...agent,
        startTime,
        endTime,
        is_allocated: 1,
        priority: (() => {
          const val = agentPriorities[String(agent.id)];
          const num = parseInt(val, 10);
          return isNaN(num) ? 10 : num;
        })(),
      }));

    if (selectedAgents.length === 0) {
      toast.error('Please select at least one agent before adding a member.');
      return;
    }

    const errors: any = {};
    let hasPriorityError = false;
    selectedAgentIds.forEach((id) => {
      const val = agentPriorities[String(id)];
      if (val === undefined || val === '') {
        setAgentPriorities((prev) => ({ ...prev, [String(id)]: '10' }));
        return;
      }
      const num = parseInt(val, 10);
      if (isNaN(num) || num < 0 || num > 10) {
        errors[String(id)] = true;
        hasPriorityError = true;
      }
    });

    if (hasPriorityError) {
      setAddMemberPriorityErrors(errors);
      toast.error('Priority (0-10) is required for all selected agents');
      return;
    }

    const AddmemberPayload = {
      type: 'add_member',
      agent_id: selectedAgents.map((data) => data.id),
      member_count: selectedAgents?.length,
      priorities: selectedAgents.reduce((acc: any, a) => {
        const val = agentPriorities[String(a.id)];
        const num = parseInt(val, 10);
        acc[a.id] = isNaN(num) ? 10 : num;
        return acc;
      }, {}),
      memberSchedulePayload: {
        week_days: memberWeekDays,
        start_time: startTime,
        end_time: endTime,
      },
    };

    try {
      setSavingMembers(true);
      const [addMembersResponse] = await Promise.all([
        postApiCall(API_SINGLE_AGENT_GROUP.replace(':id', groupId), AddmemberPayload),
      ]);
      if (addMembersResponse) {
        showToast('Members added, schedules updated, and allocation updated successfully');
        setAssignedAgents((prev) => [...prev, ...selectedAgents]);
        setAvailableAgents((prev) => prev.filter((agent) => !selectedAgentIds.includes(agent.id)));
        setSelectedAgentIds([]);
        setAgentPriorities({});
        setAddMemberPriorityErrors({});
        setShowAddMembersModal(false);
        await fetchAgentData();
      } else {
        toast.error('Failed to complete all member actions. Please try again.');
      }
    } catch (error: any) {
      console.error('Error in handleAddMembersSave:', error);
      toast.error(friendlyApiError(error) || 'Something went wrong');
    } finally {
      setSavingMembers(false);
    }
  };

  const handleSticky = (value) => setSticky(value);
  const handleMusicOnHold = (value) => {
    if (value) setShowMusicOnHold(true);
    else {
      setSelectedMusicHold(null);
      setShowMusicOnHold(false);
    }
  };
  const handleStickyMemberEnabledChange = (enabled) => {
    setStickyMemberEnabled(enabled);
    if (enabled && stickyMemberMode !== STICKY_HARD && stickyMemberMode !== STICKY_SOFT) {
      setStickyMemberMode(STICKY_SOFT);
    }
  };
  const handleStickyMemberModeChange = (value) => setStickyMemberMode(value);

  const fetchCallStratergies = () => {
    getApiCall(API_CALL_STRATEGIES_MASTER_LIST)
      .then((data: any) => setCallStratergy(data))
      .catch(() => toast.error('Error fetching call stratergies'));
  };

  const fetchCallScripts = async () => {
    setLoadingCallScripts(true);
    try {
      const resp: any = await getApiCall(API_CALL_SCRIPT_LIST);
      const list = Array.isArray(resp) ? resp : resp?.data ?? [];
      const filtered = list.filter((s) => s && s.created_by);
      setCallScripts(filtered);
    } catch (err) {
      console.error('Error fetching call scripts:', err);
      setCallScripts([]);
    } finally {
      setLoadingCallScripts(false);
    }
  };

  const handleCallScriptClick = () => {
    setPendingCallScriptId(selectedCallScriptId || '');
    setShowCallScriptModal(true);
  };
  const handleCallScriptClose = () => {
    if (savingCallScript) return;
    setShowCallScriptModal(false);
    setPendingCallScriptId('');
  };

  const handleCallScriptSave = async () => {
    if (savingCallScript) return;
    const trimmed = pendingCallScriptId === '' || pendingCallScriptId == null ? null : String(pendingCallScriptId);
    if (trimmed !== null) {
      const exists = callScripts.some((s) => String(s.id) === trimmed);
      if (!exists) {
        toast.error('Selected call script is not valid.');
        return;
      }
    }
    const finalPayload = { script_id: trimmed === null ? null : Number(trimmed) };
    try {
      setSavingCallScript(true);
      const response = await postApiCall(API_SINGLE_AGENT_GROUP.replace(':id', groupId), finalPayload);
      if (response) {
        setSelectedCallScriptId(trimmed === null ? '' : trimmed);
        showToast(trimmed === null ? 'Call Script removed' : 'Call Script updated');
        setShowCallScriptModal(false);
        await getAgentGroupData();
      }
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Failed to update Call Script');
    } finally {
      setSavingCallScript(false);
    }
  };

  const handleCallScriptRemove = async () => {
    if (savingCallScript) return;
    if (!selectedCallScriptId) {
      toast.info('No Call Script attached');
      return;
    }
    setPendingCallScriptId('');
    try {
      setSavingCallScript(true);
      const response = await postApiCall(API_SINGLE_AGENT_GROUP.replace(':id', groupId), { script_id: null });
      if (response) {
        setSelectedCallScriptId('');
        showToast('Call Script removed');
        setShowCallScriptModal(false);
        await getAgentGroupData();
      }
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Failed to remove Call Script');
    } finally {
      setSavingCallScript(false);
    }
  };

  const fetchDispositionForms = async () => {
    setLoadingDispositions(true);
    try {
      const resp: any = await getApiCall(API_DISPOSITION_LIST, { submissions: 0 });
      const list = Array.isArray(resp) ? resp : resp?.data ?? [];
      const filtered = list.filter((d) => d && d.created_by);
      setDispositionForms(filtered);
    } catch (err) {
      console.error('Error fetching disposition forms:', err);
      setDispositionForms([]);
    } finally {
      setLoadingDispositions(false);
    }
  };

  const dispositionFlowConfig: any = {
    inbound: {
      payloadField: 'disposition_id_inbound',
      currentValue: selectedDispositionIdInbound,
      setCurrentValue: setSelectedDispositionIdInbound,
      label: 'Inbound Disposition Form',
    },
    outbound: {
      payloadField: 'disposition_id_outbound',
      currentValue: selectedDispositionIdOutbound,
      setCurrentValue: setSelectedDispositionIdOutbound,
      label: 'Outbound Disposition Form',
    },
  };

  const handleDispositionClick = (flow) => {
    const cfg = dispositionFlowConfig[flow];
    if (!cfg) return;
    if (cfg.currentValue) {
      setPendingDispositionId(cfg.currentValue);
    } else {
      const def =
        dispositionForms.find((d) => d.is_default && String(d.call_direction) === flow) ||
        dispositionForms.find((d) => d.is_default && String(d.call_direction) === 'both');
      setPendingDispositionId(def ? String(def.id) : '');
    }
    setDispositionModalFlow(flow);
    setShowDispositionModal(true);
  };

  const handleDispositionClose = () => {
    if (savingDisposition) return;
    setShowDispositionModal(false);
    setPendingDispositionId('');
  };

  const handleDispositionSave = async () => {
    if (savingDisposition) return;
    const cfg = dispositionFlowConfig[dispositionModalFlow];
    if (!cfg) return;
    const trimmed = pendingDispositionId === '' || pendingDispositionId == null ? null : String(pendingDispositionId);
    if (trimmed !== null) {
      const picked = dispositionForms.find((d) => String(d.id) === trimmed);
      if (!picked) {
        toast.error('Selected disposition form is not valid.');
        return;
      }
      if (!formMatchesCallDirection(picked.call_direction, dispositionModalFlow)) {
        toast.error(
          `Selected form's call flow (${formatDispositionCallDirectionLabel(picked.call_direction)}) must match ${String(dispositionModalFlow).toUpperCase()} or Both`,
        );
        return;
      }
    }
    const finalPayload: any = { [cfg.payloadField]: trimmed === null ? null : Number(trimmed) };
    try {
      setSavingDisposition(true);
      const response = await postApiCall(API_SINGLE_AGENT_GROUP.replace(':id', groupId), finalPayload);
      if (response) {
        cfg.setCurrentValue(trimmed === null ? '' : trimmed);
        showToast(trimmed === null ? `${cfg.label} removed` : `${cfg.label} updated`);
        setShowDispositionModal(false);
        await getAgentGroupData();
      }
    } catch (err: any) {
      toast.error(friendlyApiError(err) || `Failed to update ${cfg.label}`);
    } finally {
      setSavingDisposition(false);
    }
  };

  const handleDispositionRemove = async (flowOverride = null) => {
    if (savingDisposition) return;
    const flow = flowOverride || dispositionModalFlow;
    const cfg = dispositionFlowConfig[flow];
    if (!cfg) return;
    if (!cfg.currentValue) {
      toast.info(`No ${cfg.label} attached`);
      return;
    }
    setPendingDispositionId('');
    try {
      setSavingDisposition(true);
      const response = await postApiCall(API_SINGLE_AGENT_GROUP.replace(':id', groupId), { [cfg.payloadField]: null });
      if (response) {
        cfg.setCurrentValue('');
        showToast(`${cfg.label} removed`);
        setShowDispositionModal(false);
        await getAgentGroupData();
      }
    } catch (err: any) {
      toast.error(friendlyApiError(err) || `Failed to remove ${cfg.label}`);
    } finally {
      setSavingDisposition(false);
    }
  };

  const loadAvailableInboundNumbers = async () => {
    if (!groupId || !canInboundNumbers) return;
    setLoadingAvailableNumbers(true);
    try {
      const list: any = await getApiCall(API_AGENT_GROUP_AVAILABLE_INBOUND_NUMBERS.replace(':id', groupId));
      setAvailableInboundNumbers(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setAvailableInboundNumbers([]);
    } finally {
      setLoadingAvailableNumbers(false);
    }
  };

  const handleInboundNumberSelect = (values) => {
    let ids = (values || []).map((v) => Number(v)).filter((v) => Number.isFinite(v));
    // Adding stops at the limit ("Select all" keeps the first ones); removing is always fine,
    // even when a group already has more than the limit.
    if (ids.length > MAX_GROUP_CLIS && ids.length > selectedInboundNumberIds.length) {
      const kept = selectedInboundNumberIds.filter((id) => ids.includes(id));
      const room = Math.max(0, MAX_GROUP_CLIS - kept.length);
      ids = [...kept, ...ids.filter((id) => !kept.includes(id)).slice(0, room)];
      toast.error(`You can select up to ${MAX_GROUP_CLIS} numbers for a call group.`);
    }
    setSelectedInboundNumberIds((prev) => {
      const kept = prev.filter((id) => ids.includes(id));
      const appended = ids.filter((id) => !kept.includes(id));
      return [...kept, ...appended];
    });
    setDedicatedInboundNumberId((prev) => (prev != null && ids.includes(prev) ? prev : null));
  };

  const handleSaveInboundNumberAssignment = async () => {
    if (!agentGroup?.group_name || !groupId || savingInboundRouting) return;
    if (selectedInboundNumberIds.length > MAX_GROUP_CLIS) {
      toast.error(
        `You can select up to ${MAX_GROUP_CLIS} numbers for a call group. Remove ${selectedInboundNumberIds.length - MAX_GROUP_CLIS} to save.`,
      );
      return;
    }
    setSavingInboundRouting(true);
    try {
      await postApiCall(API_AGENT_GROUP_CLI_ALLOCATIONS_SYNC.replace(':id', groupId), {
        numberIds: selectedInboundNumberIds,
        defaultNumberId: selectedInboundNumberIds?.[0] || null,
        dedicatedNumberIds: dedicatedInboundNumberId != null ? [dedicatedInboundNumberId] : [],
      });
      toast.success('Numbers saved');
      await loadAvailableInboundNumbers();
    } catch (error: any) {
      toast.error(friendlyApiError(error) || 'Failed to save numbers');
    } finally {
      setSavingInboundRouting(false);
    }
  };

  useEffect(() => {
    if (!groupId) return;
    const fetchData = async () => {
      await fetchAgentData();
      fetchCallStratergies();
      fetchCallScripts();
      fetchDispositionForms();
      await getAgentGroupData();
    };
    fetchData();
    fetchAndSetFlashMessage(
      (msg) =>
        msg.module_name === MODULE_ACL.CALL_GROUP &&
        msg.action === FLASH_MESSAGE_ACTION_OBJECT.EDIT &&
        msg.type === 'Note',
      setNotes,
    );
  }, [groupId]);

  useEffect(() => {
    setAvailableInboundNumbers([]);
    setSelectedInboundNumberIds([]);
    setDedicatedInboundNumberId(null);
    if (!groupId || !canInboundNumbers) return;
    loadAvailableInboundNumbers();
  }, [groupId, canInboundNumbers]);

  useEffect(() => {
    if (!availableInboundNumbers.length || !groupId) return;
    const inGroupCli = availableInboundNumbers.filter((n) => isNumberAssignedToCallGroup(n, groupId));
    if (inGroupCli.length) {
      setSelectedInboundNumberIds(inGroupCli.map((b) => Number(b.id)).filter((id) => Number.isFinite(id)));
      const dedicatedMatch = inGroupCli.find(
        (n) => n.inbound_routing_type === 'QUEUE' && String(n.inbound_routing_id) === String(groupId),
      );
      setDedicatedInboundNumberId(
        dedicatedMatch && Number.isFinite(Number(dedicatedMatch.id)) ? Number(dedicatedMatch.id) : null,
      );
    } else {
      setSelectedInboundNumberIds([]);
      setDedicatedInboundNumberId(null);
    }
  }, [availableInboundNumbers, groupId]);

  const handleRemoveAgent = async (agentId) => {
    try {
      await postApiCall(`/v2/agent-group/remove-agent/${groupId}`, { agent_id: agentId });
      showToast('Agent removed successfully');
      await fetchAgentData();
    } catch (error) {
      toast.error(friendlyApiError(error) || 'Failed to remove agent');
    }
  };

  const priorityDraftValue = (agent) => {
    const id = String(agent?.agent_id);
    if (Object.prototype.hasOwnProperty.call(priorityDrafts, id)) return String(priorityDrafts[id]);
    return String(memberPriorityNumber(agent));
  };

  const hasUnsavedPriorityChanges = assignedAgents.some((agent) => {
    const id = String(agent?.agent_id);
    if (!Object.prototype.hasOwnProperty.call(priorityDrafts, id)) return false;
    return String(priorityDrafts[id]).trim() !== String(memberPriorityNumber(agent));
  });

  const handlePriorityDraftChange = (agent, value) => {
    const id = String(agent?.agent_id);
    setPriorityDrafts((prev) => {
      const next = { ...prev, [id]: value };
      if (String(value).trim() === String(memberPriorityNumber(agent))) delete next[id];
      return next;
    });
    setPriorityRowErrors({});
  };

  const saveAssignedPriorities = async () => {
    if (savingPriorities || !hasUnsavedPriorityChanges) return;

    const resolved = assignedAgents.map((agent) => {
      const id = String(agent.agent_id);
      const raw = priorityDraftValue(agent).trim();
      const num = parseInt(raw, 10);
      const changed =
        Object.prototype.hasOwnProperty.call(priorityDrafts, id) &&
        raw !== String(memberPriorityNumber(agent));
      return { agent, id, raw, num, changed };
    });

    const nextErrors: any = {};
    resolved.forEach((row) => {
      if (!row.changed) return;
      if (row.raw === '' || Number.isNaN(row.num) || row.num < 0 || row.num > 10) {
        nextErrors[row.id] = 'Priority must be a number between 0 and 10';
      }
    });

    setPriorityRowErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      toast.error('Fix the priority errors before saving.');
      return;
    }

    const changed = resolved.filter((row) => row.changed);
    try {
      setSavingPriorities(true);
      for (const row of changed) {
        await postApiCall(`/v2/agent-group/schedule/${groupId}`, {
          agent_id: row.agent.agent_id,
          id: row.agent.schedule?.id,
          start_time: row.agent.schedule?.start_time || DEFAULT_SCHEDULE_START,
          end_time: row.agent.schedule?.end_time || DEFAULT_SCHEDULE_END,
          week_days: memberWeekDayList(row.agent?.schedule?.week_days),
          priority: row.num,
        });
      }
      setPriorityDrafts({});
      setPriorityRowErrors({});
      showToast('Priorities updated');
      await fetchAgentData();
    } catch (error) {
      toast.error(friendlyApiError(error) || 'Failed to update priority');
    } finally {
      setSavingPriorities(false);
    }
  };

  const handleSaveAgentEdit = async () => {
    if (savingAgentSchedule) return;
    const priorityText = editData.priority === 0 || editData.priority === '0' ? '0' : String(editData.priority ?? '');
    if (priorityText.trim() === '') {
      setPriorityError('Priority is required and must be a number between 0 and 10');
      toast.error('Priority is required and must be a number between 0 and 10');
      return;
    }
    const priorityNum = parseInt(priorityText, 10);
    if (isNaN(priorityNum) || priorityNum < 0 || priorityNum > 10) {
      setPriorityError('Priority must be a number between 0 and 10');
      toast.error('Priority must be a number between 0 and 10');
      return;
    }

    const finalPayload = {
      agent_id: editData.agent_id,
      id: editData.id,
      start_time: editData.startTime,
      end_time: editData.endTime,
      week_days: Array.isArray(editData.weekDays) ? editData.weekDays : [],
      priority: priorityNum,
    };

    if (finalPayload.week_days == 0) {
      toast.error('Please select atleast one week day');
      return;
    }

    if (!(editData.startTime === '00:00' && editData.endTime === '23:59')) {
      const startMins = toMinutes(editData.startTime);
      const endMins = toMinutes(editData.endTime);
      if (!Number.isFinite(startMins) || !Number.isFinite(endMins)) {
        toast.error('Invalid time selected');
        return;
      }
      if (editData.weekDays.length === 1 && endMins <= startMins) {
        toast.error('For a single selected day, end time should be greater than start time');
        return;
      }
    }

    try {
      setSavingAgentSchedule(true);
      const response = await postApiCall(`/v2/agent-group/schedule/${groupId}`, finalPayload);
      if (response) {
        showToast('Member schedule and priority updated');
        setShowEditModal(false);
        setPriorityDrafts((prev) => {
          const next = { ...prev };
          delete next[String(editData.agent_id)];
          return next;
        });
        setPriorityRowErrors((prev) => {
          const next = { ...prev };
          delete next[String(editData.agent_id)];
          return next;
        });
        await fetchAgentData();
      }
    } catch (err) {
      console.error('Error updating agent time schedule:', err);
      toast.error(friendlyApiError(err) || 'Failed to update agent time schedule');
    } finally {
      setSavingAgentSchedule(false);
    }
  };

  const handlesave = async () => {
    if (savingMain) return;
    if (!canSaveMainSettings) {
      toast.error('You do not have permission to change these call group settings.');
      return;
    }
    let mohIdToSend: any = null;
    if (canWaitingAudio && selectedMusicHold !== null && selectedMusicHold !== undefined && selectedMusicHold !== '') {
      const mohId = Number(selectedMusicHold);
      if (!Number.isInteger(mohId) || mohId <= 0) {
        toast.error('Invalid MOH selected');
        return;
      }
      const exists = mohOptions.some((m) => Number(m.id) === mohId);
      if (!exists) {
        toast.error('Selected MOH is not approved or not owned by you. Please pick from the list.');
        return;
      }
      mohIdToSend = mohId;
    }

    if (canEditCallGroup && queueTimingInvalid) {
      toast.error(ringTimeoutError || maxWaitError);
      return;
    }

    const ringTimeout = Number(allAgentRingingTimeout);
    const waitTime = Number(maxWaitTime);

    const finalPayload: any = {};
    if (canEditCallGroup) {
      finalPayload.should_wait_or_hangup = shouldWaitOrHangup === QUEUE_HANGUP ? QUEUE_HANGUP : QUEUE_WAIT;
      finalPayload.all_agent_ringing_timeout = ringTimeout;
      finalPayload.max_wait_time = waitTime;
    }
    if (canMultiIvrSticky) finalPayload.sticky = sticky;
    if (canSequentialForwarding) {
      finalPayload.sticky_member = stickyMemberEnabled ? stickyMemberMode : null;
    }
    if (canWaitingAudio) finalPayload.music_on_hold = mohIdToSend;
    if (canVoicemailEnabled) finalPayload.is_voicemail_enabled = !!voicemailEnabled;
    if (canNoAgentPrompt && shouldWaitOrHangup === QUEUE_HANGUP) {
      const promptId =
        selectedNoAgentPromptId && selectedNoAgentPromptId !== '' ? Number(selectedNoAgentPromptId) : null;
      if (promptId !== null) {
        const exists = noAgentPromptOptions.some((p) => Number(p.id) === Number(promptId));
        if (!exists) {
          toast.error('Selected prompt is not approved for this call center.');
          return;
        }
        if (!selectedNoAgentPromptLanguage) {
          toast.error('Please select the language for the no agent prompt.');
          return;
        }
      }
      finalPayload.no_agent_available_prompt_id = promptId;
      finalPayload.no_agent_available_prompt_language = promptId !== null ? selectedNoAgentPromptLanguage : null;
    }

    if (Object.keys(finalPayload).length === 0) {
      toast.error('You do not have permission to save these call group settings.');
      return;
    }

    try {
      setSavingMain(true);
      await postApiCall(API_SINGLE_AGENT_GROUP.replace(':id', groupId), finalPayload);
      toast.success('Call group settings updated successfully', {
        description: 'If this call group is used in an active campaign, changes may take up to 5 minutes to fully sync.',
      });
      await getAgentGroupData();
    } catch (error: any) {
      console.error('Error saving settings:', error);
      toast.error(friendlyApiError(error) || 'Failed to save settings');
    } finally {
      setSavingMain(false);
    }
  };

  const handleWhisperSave = async () => {
    if (savingWhisper) return;
    const whisperId = selectedWhisper && selectedWhisper !== '' ? selectedWhisper : null;
    if (whisperId !== null && !selectedWhisperLanguage) {
      toast.error('Please select the language for the whisper prompt.');
      return;
    }
    const finalPayload = {
      whisper_id: whisperId,
      whisper_language: whisperId !== null ? selectedWhisperLanguage : null,
    };
    try {
      setSavingWhisper(true);
      const response = await postApiCall(API_SINGLE_AGENT_GROUP.replace(':id', groupId), finalPayload);
      if (response) {
        showToast('Whisper Updated');
        setShowWhisperModal(false);
        getAgentGroupData();
      }
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Failed to update whisper');
    } finally {
      setSavingWhisper(false);
    }
  };

  const handleNoAgentPromptSave = async () => {
    if (savingNoAgentPrompt) return;
    const promptId = selectedNoAgentPromptId && selectedNoAgentPromptId !== '' ? Number(selectedNoAgentPromptId) : null;
    if (promptId !== null) {
      const exists = noAgentPromptOptions.some((p) => Number(p.id) === Number(promptId));
      if (!exists) {
        toast.error('Selected prompt is not approved for this call center.');
        return;
      }
      if (!selectedNoAgentPromptLanguage) {
        toast.error('Please select the language for the no agent prompt.');
        return;
      }
    }
    try {
      setSavingNoAgentPrompt(true);
      const payload = {
        no_agent_available_prompt_id: promptId,
        no_agent_available_prompt_language: promptId !== null ? selectedNoAgentPromptLanguage : null,
      };
      const response = await postApiCall(API_SINGLE_AGENT_GROUP.replace(':id', groupId), payload);
      if (response) {
        showToast('No-agent-available prompt updated');
        setShowNoAgentPromptModal(false);
        await getAgentGroupData();
      }
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Unable to save no-agent prompt');
    } finally {
      setSavingNoAgentPrompt(false);
    }
  };

  const handleCustomerDisclaimerToggle = async (nextEnabled) => {
    if (savingCustomerDisclaimer) return;
    if (nextEnabled) {
      setShowCustomerDisclaimerModal(true);
      return;
    }
    try {
      setSavingCustomerDisclaimer(true);
      const response = await postApiCall(API_SINGLE_AGENT_GROUP.replace(':id', groupId), {
        customer_disclaimer_enabled: false,
        customer_disclaimer_prompt_id: null,
        customer_disclaimer_language: null,
      });
      if (response) {
        showToast('Customer disclaimer turned off');
        setShowCustomerDisclaimerModal(false);
        await getAgentGroupData();
      }
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Unable to update customer disclaimer');
    } finally {
      setSavingCustomerDisclaimer(false);
    }
  };

  const handleCustomerDisclaimerSave = async () => {
    if (savingCustomerDisclaimer) return;
    if (!selectedCustomerDisclaimerPromptId) {
      toast.error('Please select the customer disclaimer prompt.');
      return;
    }
    if (!selectedCustomerDisclaimerLanguage) {
      toast.error('Please select the language for the customer disclaimer prompt.');
      return;
    }
    try {
      setSavingCustomerDisclaimer(true);
      const response = await postApiCall(API_SINGLE_AGENT_GROUP.replace(':id', groupId), {
        customer_disclaimer_enabled: true,
        customer_disclaimer_prompt_id: Number(selectedCustomerDisclaimerPromptId),
        customer_disclaimer_language: selectedCustomerDisclaimerLanguage,
      });
      if (response) {
        showToast('Customer disclaimer updated');
        setShowCustomerDisclaimerModal(false);
        await getAgentGroupData();
      }
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Unable to save customer disclaimer');
    } finally {
      setSavingCustomerDisclaimer(false);
    }
  };

  const handleMusicHoldSave = () => {
    setShowMusicOnHold(false);
  };

  if (isLoading || !isDataLoaded) {
    return (
      <div style={{ marginTop: '20px' }}>
        <Row>
          <Col sm="12">
            <Card>
              <Card.Body>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                  <div
                    className="spinner-border text-primary"
                    role="status"
                    style={{ width: '3rem', height: '3rem' }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  const navTileStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: '1 1 0',
    minWidth: 0,
    width: '100%',
    background: 'hsl(var(--card))',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '10px 12px',
    textAlign: 'left' as const,
    cursor: 'pointer',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  };
  const navIconWrapStyle = (bg: string) => ({
    width: 28,
    height: 28,
    borderRadius: 6,
    background: bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  });
  const renderNavTile = (opts: {
    id: string;
    title: string;
    subtitle: string;
    tooltip: string;
    onClick: () => void;
    icon: React.ReactNode;
    iconBg: string;
  }) => (
    <OverlayTrigger
      key={opts.id}
      placement="top"
      overlay={<Tooltip id={`${opts.id}-tooltip`}>{opts.tooltip}</Tooltip>}
    >
      <button type="button" onClick={opts.onClick} style={navTileStyle}>
        <span style={navIconWrapStyle(opts.iconBg)}>{opts.icon}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              fontWeight: 600,
              fontSize: 12,
              color: '#0f172a',
              lineHeight: 1.25,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {opts.title}
          </span>
          <span
            style={{
              display: 'block',
              fontSize: 10,
              color: '#64748b',
              marginTop: 1,
              lineHeight: 1.25,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {opts.subtitle}
          </span>
        </span>
        <ChevronRight size={16} color="#94a3b8" style={{ flexShrink: 0 }} />
      </button>
    </OverlayTrigger>
  );

  // Each setting is its own box in the routing-basics grey, on a lighter panel.
  const settingSectionStyle = {
    background: 'hsl(var(--card))',
    border: '1px solid #d7dee8',
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
    borderRadius: '12px',
    padding: '20px 24px',
    marginBottom: '16px',
  };
  const settingLabelStyle = {
    fontWeight: 600,
    fontSize: '14px',
    color: '#1e293b',
    marginBottom: '6px',
  };
  const settingNoteStyle = {
    fontSize: '12px',
    color: '#64748b',
    lineHeight: 1.55,
    marginTop: '6px',
    marginBottom: 0,
  };
  const radioRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap' as const,
  };
  const timeoutInputStyle = {
    maxWidth: '160px',
    height: '38px',
  };
  const fieldErrorStyle = {
    fontSize: '12px',
    color: '#dc2626',
    marginTop: '6px',
    marginBottom: 0,
  };

  const isQueueWait = shouldWaitOrHangup === QUEUE_WAIT;
  const isQueueHangup = shouldWaitOrHangup === QUEUE_HANGUP;
  const ringTimeoutError = canEditCallGroup ? getRingTimeoutError(allAgentRingingTimeout) : '';
  const maxWaitError =
    canEditCallGroup && isQueueWait ? getMaxWaitError(maxWaitTime, allAgentRingingTimeout) : '';
  const queueTimingInvalid = Boolean(ringTimeoutError || maxWaitError);
  const showQueueOutcomeSection =
    canEditCallGroup || (canNoAgentPrompt && isQueueHangup);

  return (
    <ScrollPage className="max-w-none">
      <PageHeader
        title="Group Settings"
        description="Configure IVR routing, agents, and time-schedules for this call group."
      />
      <Row>
        <Col sm="12">
          {canInboundNumbers && groupId ? (
            <Card style={{ border: '1px solid #e2e8f0', marginBottom: '20px' }}>
              <Card.Body style={{ padding: '24px 28px 28px' }}>
                <h5
                  style={{
                    fontWeight: 700,
                    fontSize: '16px',
                    color: '#0f172a',
                    marginBottom: '24px',
                  }}
                >
                  Routing basics
                </h5>
                <div className="flex flex-col lg:flex-row lg:items-stretch">
                  <div className="min-w-0 flex-1 pb-4 lg:flex-[1.4] lg:border-r lg:border-[#e2e8f0] lg:pb-0 lg:pr-8">
                    <Form.Label
                      className="d-flex align-items-center w-100"
                      style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b', marginBottom: '8px' }}
                    >
                      Outbound caller IDs (CLI) for this group's agents
                      <SelectAllToggle
                        allIds={
                          isInboundNumberSelectableForGroup()
                            ? availableInboundNumbers.map((n) => Number(n.id))
                            : []
                        }
                        selectedIds={selectedInboundNumberIds}
                        onChange={handleInboundNumberSelect}
                        disabled={loadingAvailableNumbers || savingInboundRouting}
                      />
                    </Form.Label>
                      <ReactSelect
                        isMulti
                        value={availableInboundNumbers
                          .filter((n) => selectedInboundNumberIds.includes(Number(n.id)))
                          .map((n) => ({
                            value: Number(n.id),
                            label: `${String(n.actual_number)} (CLI)${Number(selectedInboundNumberIds?.[0]) === Number(n.id) ? ' - default' : ''}`,
                          }))}
                        options={availableInboundNumbers
                          .filter((n) => isInboundNumberSelectableForGroup())
                          .map((n) => ({
                            value: Number(n.id),
                            label: `${String(n.actual_number)} (CLI)${Number(selectedInboundNumberIds?.[0]) === Number(n.id) ? ' - default' : ''}`,
                          }))}
                        onChange={(sel: any) => handleInboundNumberSelect((sel || []).map((o) => o.value))}
                        isOptionDisabled={() => selectedInboundNumberIds.length >= MAX_GROUP_CLIS}
                        isDisabled={loadingAvailableNumbers || savingInboundRouting}
                        placeholder={`Select numbers (max ${MAX_GROUP_CLIS})`}
                        className="w-100"
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 200000 }) }}
                      />
                    <p
                      style={{
                        fontSize: '12px',
                        marginTop: '8px',
                        marginBottom: 0,
                        color: selectedInboundNumberIds.length > MAX_GROUP_CLIS ? '#dc2626' : '#64748b',
                      }}
                    >
                      {selectedInboundNumberIds.length} of {MAX_GROUP_CLIS} selected
                      {selectedInboundNumberIds.length > MAX_GROUP_CLIS ? ' — remove some to save' : ''}
                    </p>
                    <p style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', marginBottom: 0 }}>
                      Agents in this call group can pick these numbers as caller ID for outbound calls (the first one
                      is the default). Agents with their own CLIs set use those instead. Remove all and save to clear.
                    </p>
                  </div>
                  <div className="min-w-0 flex-1 pt-1 lg:pl-8 lg:pt-0">
                    <Form.Label
                      className="d-block"
                      style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b', marginBottom: '10px' }}
                    >
                      Incoming calls (dedicated number)
                    </Form.Label>
                    <Form.Check
                      type="radio"
                      id="group-dedicated-cli-none"
                      name="groupDedicatedCli"
                      label="None"
                      checked={dedicatedInboundNumberId === null}
                      onChange={() => setDedicatedInboundNumberId(null)}
                      disabled={savingInboundRouting || !selectedInboundNumberIds?.length}
                      className="mb-2"
                    />
                    {selectedInboundNumberIds.map((nid) => {
                      const row = availableInboundNumbers.find((n) => Number(n.id) === Number(nid));
                      if (!row) return null;
                      return (
                        <Form.Check
                          key={`grp-ded-${nid}`}
                          type="radio"
                          id={`grp-dedicated-cli-${nid}`}
                          name="groupDedicatedCli"
                          label={`${row.actual_number} (CLI)`}
                          checked={dedicatedInboundNumberId === Number(nid)}
                          onChange={() => setDedicatedInboundNumberId(Number(nid))}
                          disabled={savingInboundRouting}
                          className="mb-2"
                        />
                      );
                    })}
                    <div className="d-flex align-items-start" style={{ gap: '8px', marginTop: '12px' }}>
                      <FiInfo size={16} color="#64748b" style={{ marginTop: '2px', flexShrink: 0 }} aria-hidden />
                      <span style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.55 }}>
                        Incoming calls on the chosen number ring this call group. <strong>None</strong> stops routing
                        that number here. Set its <strong>time schedule</strong> in <strong>Numbers</strong> inbound routing.
                      </span>
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-end" style={{ marginTop: '28px' }}>
                  <Button
                    variant="primary"
                    className="px-4"
                    disabled={savingInboundRouting}
                    onClick={handleSaveInboundNumberAssignment}
                  >
                    {savingInboundRouting ? 'Saving…' : 'Save numbers'}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ) : null}

          {(canMemberManagement ||
            canGroupTimeSchedule ||
            canCallStrategy ||
            canAgentWhisper ||
            canInboundDisposition ||
            canOutboundDisposition ||
            canCallScript) && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: '8px',
                width: '100%',
                marginBottom: '16px',
              }}
            >
              {canMemberManagement &&
                renderNavTile({
                  id: 'add-members',
                  title: 'Add Members',
                  subtitle: 'Manage group members',
                  tooltip:
                    'Include your member in this group to assign workflows specifically according to time schedules and call strategies.',
                  onClick: handleAddMembersClick,
                  iconBg: '#dcfce7',
                  icon: <Users size={16} color="#16a34a" />,
                })}
              {canGroupTimeSchedule &&
                renderNavTile({
                  id: 'time-schedule',
                  title: 'Time Schedule',
                  subtitle: 'Define call time periods',
                  tooltip:
                    'Account owner can configure when calls are routed to available members based on provided scheduled business hours.',
                  onClick: handleTimeScheduleClick,
                  iconBg: '#dbeafe',
                  icon: <Clock size={16} color="#2563eb" />,
                })}
              {canCallStrategy &&
                renderNavTile({
                  id: 'call-strategy',
                  title: 'Call Strategy',
                  subtitle: 'Set routing behavior',
                  tooltip:
                    'It provides a method to handle call flows for members with several extensions using a variety of routing strategies.',
                  onClick: handleCallStrategyClick,
                  iconBg: '#ede9fe',
                  icon: <GitBranch size={16} color="#7c3aed" />,
                })}
              {canAgentWhisper &&
                renderNavTile({
                  id: 'agent-whisper',
                  title: 'Agent Whisper',
                  subtitle: 'Speak to agents privately',
                  tooltip:
                    'Agent Whisper voice messages so that your team can hear this voice prompt first before connecting with the customer.',
                  onClick: handleWhisperClick,
                  iconBg: '#ffedd5',
                  icon: <Speech size={16} color="#ea580c" />,
                })}
              {canInboundDisposition &&
                renderNavTile({
                  id: 'inbound-disposition',
                  title: 'Inbound Disposition',
                  subtitle: 'Set after-call options',
                  tooltip:
                    'Attach an optional Inbound Disposition Form (your own inbound or both forms) that agents will fill after each inbound call for this group.',
                  onClick: () => handleDispositionClick('inbound'),
                  iconBg: '#ede9fe',
                  icon: <ClipboardList size={16} color="#7c3aed" />,
                })}
              {canOutboundDisposition &&
                renderNavTile({
                  id: 'outbound-disposition',
                  title: 'Outbound Disposition',
                  subtitle: 'Set after-call options',
                  tooltip:
                    'Attach an optional Outbound Disposition Form (your own outbound or both forms) that agents will fill after each outbound call for this group.',
                  onClick: () => handleDispositionClick('outbound'),
                  iconBg: '#ccfbf1',
                  icon: <Target size={16} color="#0d9488" />,
                })}
              {canCallScript &&
                renderNavTile({
                  id: 'call-script',
                  title: 'Call Script',
                  subtitle: 'Manage call scripts',
                  tooltip:
                    'Attach an optional Call Script (your own scripts only) that agents can reference for this call group.',
                  onClick: handleCallScriptClick,
                  iconBg: '#e0f2fe',
                  icon: <FileText size={16} color="#0284c7" />,
                })}
            </div>
          )}

          {/* Lighter backdrop so the routing-grey setting boxes inside stand out. */}
          <Card style={{ background: 'color-mix(in srgb, hsl(var(--card)) 70%, #ffffff)' }}>
            <Card.Body style={{ padding: '28px 32px' }}>
              {canCallScript && selectedCallScriptId
                ? (() => {
                    const attached = callScripts.find((s) => String(s.id) === String(selectedCallScriptId));
                    return (
                      <div
                        className="mb-3"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px 14px',
                          border: '1px solid #b3e5ec2b',
                          borderRadius: '8px',
                          fontSize: '13px',
                        }}
                      >
                        <strong>Attached Call Script:</strong>
                        <span>{attached?.title || `#${selectedCallScriptId}`}</span>
                        <button
                          type="button"
                          onClick={handleCallScriptClick}
                          disabled={savingCallScript}
                          style={{
                            background: 'transparent',
                            border: '1px solid #17a2b8',
                            color: '#0c5460',
                            borderRadius: '4px',
                            padding: '2px 8px',
                            fontSize: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          Change
                        </button>
                        <button
                          type="button"
                          onClick={handleCallScriptRemove}
                          disabled={savingCallScript}
                          style={{
                            background: 'transparent',
                            border: '1px solid #dc3545',
                            color: '#a71d2a',
                            borderRadius: '4px',
                            padding: '2px 8px',
                            fontSize: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          {savingCallScript ? 'Removing...' : 'Remove'}
                        </button>
                      </div>
                    );
                  })()
                : null}

              {['inbound', 'outbound'].map((flow) => {
                const flowAllowed = flow === 'inbound' ? canInboundDisposition : canOutboundDisposition;
                if (!flowAllowed) return null;
                const cfg = dispositionFlowConfig[flow];
                if (!cfg.currentValue) return null;
                const attached = dispositionForms.find((d) => String(d.id) === String(cfg.currentValue));
                const borderColor = flow === 'inbound' ? '#d6c8ee5d' : '#b9eedf3c';
                const editBorder = flow === 'inbound' ? '#6e42c158' : '#20c99658';
                const editColor = flow === 'inbound' ? '#4b2a8c69' : '#13725d2f';
                return (
                  <div
                    key={`disposition-attached-${flow}`}
                    className="mt-3"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px 12px',
                      border: `1px solid ${borderColor}`,
                      borderRadius: '8px',
                      fontSize: '13px',
                    }}
                  >
                    <strong>{cfg.label}:</strong>
                    <span>{attached?.name || `#${cfg.currentValue}`}</span>
                    {attached?.is_default ? <span className="badge bg-success">Default</span> : null}
                    <button
                      type="button"
                      onClick={() => handleDispositionClick(flow)}
                      disabled={savingDisposition}
                      style={{
                        background: 'transparent',
                        border: `1px solid ${editBorder}`,
                        color: editColor,
                        borderRadius: '4px',
                        padding: '2px 8px',
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      Change
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDispositionRemove(flow as any)}
                      disabled={savingDisposition}
                      style={{
                        background: 'transparent',
                        border: '1px solid #dc3545',
                        color: '#a71d2a',
                        borderRadius: '4px',
                        padding: '2px 8px',
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      {savingDisposition ? 'Removing...' : 'Remove'}
                    </button>
                  </div>
                );
              })}

              {showQueueOutcomeSection && (
                <div style={settingSectionStyle}>
                  <p style={settingLabelStyle}>If no agent is free / nobody answers:</p>
                  <div className="grid grid-cols-1 items-start gap-x-7 gap-y-[18px] md:grid-cols-[minmax(220px,260px)_minmax(280px,420px)]">
                    {canEditCallGroup && (
                      <>
                        <div>
                          <div className="d-flex align-items-center" style={{ minHeight: '38px', gap: '8px' }}>
                            <input
                              type="radio"
                              id="queueWait"
                              name="shouldWaitOrHangup"
                              className="form-check-input m-0"
                              checked={isQueueWait}
                              onChange={() => setShouldWaitOrHangup(QUEUE_WAIT)}
                            />
                            <label htmlFor="queueWait" className="mb-0" style={{ fontWeight: 600 }}>
                              Wait
                            </label>
                          </div>
                          <p style={{ ...settingNoteStyle, margin: '4px 0 0 28px' }}>
                            Keeps the caller in the call group until an agent is free.
                          </p>
                        </div>
                        <div>
                          {isQueueWait ? (
                            <>
                              <div className="d-flex flex-wrap align-items-center" style={{ minHeight: '38px', gap: '12px' }}>
                                <label htmlFor="maxWaitTime" className="mb-0" style={{ ...settingLabelStyle, marginBottom: 0, whiteSpace: 'nowrap' }}>
                                  Max wait time (seconds)
                                </label>
                                <input
                                  type="number"
                                  id="maxWaitTime"
                                  className={`form-control ${maxWaitError ? 'is-invalid' : ''}`}
                                  style={{ ...timeoutInputStyle, width: '120px', flex: '0 0 120px' }}
                                  min={MAX_WAIT_MIN}
                                  max={MAX_WAIT_MAX}
                                  value={maxWaitTime}
                                  onChange={(e) => setMaxWaitTime(e.target.value === '' ? '' : Number(e.target.value))}
                                  onBlur={() => {
                                    if (maxWaitTime === '') setMaxWaitTime(MAX_WAIT_DEFAULT);
                                  }}
                                />
                              </div>
                              <p style={maxWaitError ? fieldErrorStyle : { ...settingNoteStyle, margin: '4px 0 0' }}>
                                {maxWaitError ||
                                  `Maximum time the caller may wait in the call group (${MAX_WAIT_MIN}–${MAX_WAIT_MAX} seconds, default ${MAX_WAIT_DEFAULT}).`}
                              </p>
                            </>
                          ) : null}
                        </div>
                      </>
                    )}
                    {canEditCallGroup && (
                      <div>
                        <div className="d-flex align-items-center" style={{ minHeight: '38px', gap: '8px' }}>
                          <input
                            type="radio"
                            id="queueHangup"
                            name="shouldWaitOrHangup"
                            className="form-check-input m-0"
                            checked={isQueueHangup}
                            onChange={() => setShouldWaitOrHangup(QUEUE_HANGUP)}
                          />
                          <label htmlFor="queueHangup" className="mb-0" style={{ fontWeight: 600 }}>
                            Hang up
                          </label>
                        </div>
                        <p style={{ ...settingNoteStyle, margin: '4px 0 0 28px' }}>
                          Drops the call when no agent is available.
                        </p>
                      </div>
                    )}
                    <div>
                      {isQueueHangup && canNoAgentPrompt ? (
                        <>
                          <div className="d-flex flex-wrap align-items-center" style={{ minHeight: '38px', gap: '12px' }}>
                            <label htmlFor="inlineNoAgentPrompt" className="mb-0" style={{ ...settingLabelStyle, marginBottom: 0, whiteSpace: 'nowrap' }}>
                              No agent prompt
                            </label>
                            <select
                              id="inlineNoAgentPrompt"
                              className="form-select"
                              value={selectedNoAgentPromptId}
                              disabled={loadingNoAgentPromptOptions}
                              onChange={(e) => {
                                const nextId = e.target.value;
                                setSelectedNoAgentPromptId(nextId);
                                const langs = getApprovedPromptLanguages(nextId, noAgentPromptOptions);
                                setSelectedNoAgentPromptLanguage(
                                  langs.length === 1 ? langs[0].language : '',
                                );
                              }}
                            >
                              <option value="">
                                {loadingNoAgentPromptOptions ? 'Loading prompts...' : '-- Select no agent prompt --'}
                              </option>
                              {noAgentPromptOptions.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.prompt_name}
                                </option>
                              ))}
                            </select>
                          </div>
                          {selectedNoAgentPromptId ? (
                            <div className="d-flex flex-wrap align-items-center" style={{ marginTop: '10px', gap: '12px' }}>
                              <label htmlFor="inlineNoAgentPromptLanguage" className="mb-0" style={{ ...settingLabelStyle, marginBottom: 0, whiteSpace: 'nowrap' }}>
                                Prompt language
                              </label>
                              <select
                                id="inlineNoAgentPromptLanguage"
                                className="form-select"
                                value={selectedNoAgentPromptLanguage}
                                disabled={noAgentPromptLanguages.length === 0}
                                onChange={(e) => setSelectedNoAgentPromptLanguage(e.target.value)}
                              >
                                <option value="">
                                  {noAgentPromptLanguages.length === 0
                                    ? 'No approved language for this prompt'
                                    : 'Select language'}
                                </option>
                                {noAgentPromptLanguages.map((entry) => (
                                  <option key={entry.language} value={entry.language}>
                                    {String(entry.language).toUpperCase()}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}

              {(canSequentialForwarding || canMultiIvrSticky) && (
                <div className="row g-3">
                  {canSequentialForwarding && (
                    <div className={canMultiIvrSticky ? 'col-lg-7' : 'col-12'}>
                      <div style={settingSectionStyle}>
                        <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                          <div style={{ maxWidth: '70%' }}>
                            <p style={{ ...settingLabelStyle, marginBottom: '4px' }}>Sticky member</p>
                            <p style={{ ...settingNoteStyle, marginTop: 0 }}>
                              Send the caller back to the last agent they spoke to. Turn off to use the call group
                              strategy only.
                            </p>
                          </div>
                          <div className="form-check form-switch mb-0 flex-shrink-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="stickyMemberEnabled"
                              checked={stickyMemberEnabled}
                              onChange={(e) => handleStickyMemberEnabledChange(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="stickyMemberEnabled">
                              {stickyMemberEnabled ? 'On' : 'Off'}
                            </label>
                          </div>
                        </div>
                        {stickyMemberEnabled && (
                          <div className="d-flex flex-wrap" style={{ gap: '28px' }}>
                            <div>
                              <div className="form-check mb-1">
                                <input
                                  type="radio"
                                  id="softSticky"
                                  name="stickyMemberMode"
                                  className="form-check-input"
                                  checked={stickyMemberMode === STICKY_SOFT}
                                  onChange={() => handleStickyMemberModeChange(STICKY_SOFT)}
                                />
                                <label htmlFor="softSticky" className="form-check-label" style={{ fontWeight: 600 }}>
                                  Soft sticky
                                </label>
                              </div>
                              <p style={{ ...settingNoteStyle, maxWidth: '280px' }}>
                                Soft sticky prefers the last agent, then follows the rest of the group.
                              </p>
                            </div>
                            <div>
                              <div className="form-check mb-1">
                                <input
                                  type="radio"
                                  id="hardSticky"
                                  name="stickyMemberMode"
                                  className="form-check-input"
                                  checked={stickyMemberMode === STICKY_HARD}
                                  onChange={() => handleStickyMemberModeChange(STICKY_HARD)}
                                />
                                <label htmlFor="hardSticky" className="form-check-label" style={{ fontWeight: 600 }}>
                                  Hard sticky
                                </label>
                              </div>
                              <p style={{ ...settingNoteStyle, maxWidth: '320px' }}>
                                Hard sticky tries to connect only to the last agent; if they are busy, wait or hang up
                                follows the setting above.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {canMultiIvrSticky && (
                    <div className={canSequentialForwarding ? 'col-lg-5' : 'col-12'}>
                      <div style={settingSectionStyle}>
                        <div className="d-flex align-items-start justify-content-between gap-3">
                          <div>
                            <p style={{ ...settingLabelStyle, marginBottom: '4px' }}>Multi IVR Sticky</p>
                            <p style={{ ...settingNoteStyle, marginTop: 0 }}>
                              Keep the caller on the same IVR path on their next call.
                            </p>
                          </div>
                          <div className="form-check form-switch mb-0 flex-shrink-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="multiIvrOn"
                              checked={sticky === true}
                              onChange={(e) => handleSticky(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="multiIvrOn">
                              {sticky ? 'On' : 'Off'}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {canWaitingAudio && (
                <div style={settingSectionStyle}>
                  <p style={settingLabelStyle}>Music On Hold</p>
                  <div className="row g-4 align-items-start">
                    <div className="col-md-5">
                      <div style={radioRowStyle}>
                        <div className="form-check mb-0">
                          <input
                            type="radio"
                            id="musicOnHold"
                            name="music"
                            className="form-check-input"
                            checked={selectedMusicHold !== null && selectedMusicHold !== undefined}
                            onChange={() => handleMusicOnHold(true)}
                          />
                          <label htmlFor="musicOnHold" className="form-check-label">
                            Music On Hold
                          </label>
                        </div>
                        <div className="form-check mb-0">
                          <input
                            type="radio"
                            id="ringing"
                            name="music"
                            className="form-check-input"
                            checked={selectedMusicHold === null || selectedMusicHold === undefined}
                            onChange={() => handleMusicOnHold(false)}
                          />
                          <label htmlFor="ringing" className="form-check-label">
                            Ringing
                          </label>
                        </div>
                      </div>
                      {selectedMusicHold !== null && selectedMusicHold !== undefined && (
                        <div className="d-flex align-items-center mt-3">
                          <span className="me-2" style={{ fontSize: '13px', color: '#64748b' }}>
                            Selected MOH:
                          </span>
                          <span className="fw-bold me-3">
                            {mohOptions.find((m) => Number(m.id) === Number(selectedMusicHold))?.moh_name ||
                              (mohLoading ? 'Loading…' : '-')}
                          </span>
                          <Button size="sm" variant="outline-secondary" onClick={() => setShowMusicOnHold(true)}>
                            Change
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="col-md-7">
                      <div
                        className="d-flex align-items-start gap-3"
                        style={{
                          background: '#eff6ff',
                          border: '1px solid #bfdbfe',
                          borderRadius: '10px',
                          padding: '14px 16px',
                        }}
                      >
                        <FiInfo size={18} className="flex-shrink-0 mt-1" style={{ color: '#2563eb' }} aria-hidden />
                        <div>
                          <p style={{ ...settingNoteStyle, marginTop: 0 }}>
                            <strong style={{ color: '#1e293b' }}>Ringing</strong>
                            {' — '}
                            Call is ringing until an agent answers.
                          </p>
                          <p style={{ ...settingNoteStyle, marginTop: '6px' }}>
                            <strong style={{ color: '#1e293b' }}>Music on Hold</strong>
                            {' — '}
                            Call is answered immediately and the selected audio/music is played while waiting for an
                            agent.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {canVoicemailEnabled && (
                <div style={settingSectionStyle}>
                  <div className="d-flex align-items-start justify-content-between gap-3">
                    <div>
                      <p style={{ ...settingLabelStyle, marginBottom: '4px' }}>Voicemail</p>
                      <p style={{ ...settingNoteStyle, marginTop: 0 }}>Allow callers to leave a voicemail for this group.</p>
                    </div>
                    <div className="form-check form-switch mb-0 flex-shrink-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="groupVoicemailEnabled"
                        checked={!!voicemailEnabled}
                        onChange={(e) => setVoicemailEnabled(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="groupVoicemailEnabled">
                        {voicemailEnabled ? 'On' : 'Off'}
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div style={settingSectionStyle}>
                <div className="d-flex align-items-start justify-content-between gap-3">
                  <div>
                    <p style={{ ...settingLabelStyle, marginBottom: '4px' }}>Customer Disclaimer</p>
                    {customerDisclaimerEnabled ? (
                      <p style={{ ...settingNoteStyle, marginTop: 0 }}>
                        {`Prompt: ${
                          approvedPromptOptions.find((p) => String(p.id) === String(selectedCustomerDisclaimerPromptId))
                            ?.prompt_name || 'Configured prompt'
                        }`}
                        {selectedCustomerDisclaimerLanguage
                          ? ` (${String(selectedCustomerDisclaimerLanguage).toUpperCase()})`
                          : ''}
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0 ms-2"
                          onClick={() => setShowCustomerDisclaimerModal(true)}
                        >
                          Change
                        </button>
                      </p>
                    ) : (
                      <p style={{ ...settingNoteStyle, marginTop: 0 }}>
                        Turn on to play an approved disclaimer prompt to the customer.
                      </p>
                    )}
                  </div>
                  <div className="form-check form-switch mb-0 flex-shrink-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="groupCustomerDisclaimerEnabled"
                      checked={!!customerDisclaimerEnabled}
                      disabled={savingCustomerDisclaimer}
                      onChange={(e) => handleCustomerDisclaimerToggle(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="groupCustomerDisclaimerEnabled">
                      {customerDisclaimerEnabled ? 'On' : 'Off'}
                    </label>
                  </div>
                </div>
                <div
                  className="d-flex align-items-start gap-3 mt-3"
                  style={{
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '10px',
                    padding: '12px 16px',
                  }}
                >
                  <FiInfo size={16} className="flex-shrink-0 mt-1" style={{ color: '#2563eb' }} aria-hidden />
                  <p style={{ ...settingNoteStyle, marginTop: 0 }}>
                    Note: The disclaimer keeps playing to the customer only while the call has not entered a call group. Once
                    the call enters a call group, the call group&apos;s own settings take over immediately.
                  </p>
                </div>
              </div>

              {canEditCallGroup && (
                <div style={settingSectionStyle}>
                  <label htmlFor="allAgentRingingTimeout" style={settingLabelStyle}>
                    Agent ringing timeout (seconds)
                  </label>
                  <input
                    type="number"
                    id="allAgentRingingTimeout"
                    className={`form-control ${ringTimeoutError ? 'is-invalid' : ''}`}
                    style={{ ...timeoutInputStyle, maxWidth: '360px' }}
                    min={RING_TIMEOUT_MIN}
                    max={RING_TIMEOUT_MAX}
                    value={allAgentRingingTimeout}
                    onChange={(e) =>
                      setAllAgentRingingTimeout(e.target.value === '' ? '' : Number(e.target.value))
                    }
                    onBlur={() => {
                      if (allAgentRingingTimeout === '') setAllAgentRingingTimeout(RING_TIMEOUT_DEFAULT);
                    }}
                  />
                  <p style={ringTimeoutError ? fieldErrorStyle : settingNoteStyle}>
                    {ringTimeoutError ||
                      `How long each agent’s phone rings before trying the next agent (${RING_TIMEOUT_MIN}–${RING_TIMEOUT_MAX}, default ${RING_TIMEOUT_DEFAULT}).`}
                  </p>
                </div>
              )}

              {canSaveMainSettings && (
                <div className="d-flex justify-content-end" style={{ padding: '8px 0 4px' }}>
                  <button
                    className="btn btn-primary px-4"
                    onClick={handlesave}
                    disabled={savingMain || (canEditCallGroup && queueTimingInvalid)}
                  >
                    {savingMain ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div
        className="mt-4"
        style={{
          background: 'hsl(var(--card))',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '24px 28px',
          position: 'relative',
          zIndex: 2,
          pointerEvents: 'auto',
          flexShrink: 0,
        }}
      >
        <h5 style={{ fontWeight: 600, marginBottom: '6px' }}>Assigned Agents</h5>
        <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px' }}>
          Change a member’s priority in the Priority column, then use Save changes under the tables. Edit opens that member’s schedule.
        </p>
        {(() => {
          const teamLeadAssigned = assignedAgents.filter((a) => a.access === 'team_lead');
          const regularAssigned = assignedAgents.filter((a) => a.access !== 'team_lead');

          const tableCellStyle = { padding: '12px 16px', verticalAlign: 'middle' as const };
          const memberActionButtonStyle = {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            padding: 0,
            border: 'none',
            borderRadius: '6px',
            background: 'transparent',
            cursor: 'pointer',
            position: 'relative' as const,
            zIndex: 1,
          };
          const renderAssignedRows = (list) =>
            list.map((agent, index) => (
              <tr key={agent?.agent_id}>
                <td style={tableCellStyle}>{index + 1}</td>
                <td style={tableCellStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{agent?.agent_name}</span>
                    <button
                      type="button"
                      aria-label={`Edit ${agent?.agent_name || 'member'}`}
                      title="Edit member"
                      style={{
                        pointerEvents: 'auto',
                        position: 'relative',
                        zIndex: 5,
                        border: '1px solid #cbd5e1',
                        background: '#ffffff',
                        borderRadius: '6px',
                        padding: '4px 10px',
                        fontSize: '12px',
                        lineHeight: 1.2,
                        fontWeight: 600,
                        color: '#1e293b',
                        cursor: 'pointer',
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openMemberEditor(agent);
                      }}
                    >
                      Edit
                    </button>
                  </div>
                </td>
                <td style={tableCellStyle}>{agent?.agent_number}</td>
                <td style={tableCellStyle}>
                  <div style={{ display: 'flex', gap: '4px', justifyContent: 'start', flexWrap: 'wrap' }}>
                    {agent?.agentTelephony?.profile?.map((item) => (
                      <button
                        key={item.type}
                        className={`btn btn-sm ${item.active_profile ? 'btn-success' : 'btn-danger'}`}
                        style={{ padding: '2px 6px', fontSize: '10px', minWidth: '50px' }}
                      >
                        {item.type}
                      </button>
                    ))}
                  </div>
                </td>
                <td style={tableCellStyle}>{agent?.schedule?.start_time || '00:00'}</td>
                <td style={tableCellStyle}>{agent?.schedule?.end_time || '23:59'}</td>
                <td style={tableCellStyle}>
                  <input
                    type="number"
                    min={0}
                    max={10}
                    aria-label={`Priority for ${agent?.agent_name || 'member'}`}
                    value={priorityDraftValue(agent)}
                    disabled={savingPriorities}
                    style={{
                      width: '76px',
                      pointerEvents: 'auto',
                      position: 'relative',
                      zIndex: 5,
                      padding: '4px 8px',
                      border: priorityRowErrors[String(agent?.agent_id)] ? '1px solid #dc3545' : '1px solid #cbd5e1',
                      borderRadius: '6px',
                      background: priorityRowErrors[String(agent?.agent_id)] ? '#fff5f5' : '#ffffff',
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => handlePriorityDraftChange(agent, e.target.value)}
                  />
                  {priorityRowErrors[String(agent?.agent_id)] && (
                    <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '4px', maxWidth: '140px' }}>
                      {priorityRowErrors[String(agent?.agent_id)]}
                    </div>
                  )}
                </td>
                <td style={tableCellStyle}>
                  <div className="actions" style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                    <button
                      type="button"
                      aria-label={`Edit ${agent?.agent_name || 'member'}`}
                      title="Edit member"
                      style={{ ...memberActionButtonStyle, color: '#6c757d', pointerEvents: 'auto' }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openMemberEditor(agent);
                      }}
                    >
                      <FiEdit size={18} />
                    </button>
                    <button
                      type="button"
                      aria-label={`Remove ${agent?.agent_name || 'member'}`}
                      title="Delete"
                      style={{ ...memberActionButtonStyle, color: '#dc3545' }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemoveAgent(agent?.agent_id);
                      }}
                    >
                      <FiTrash2 size={18} style={{ pointerEvents: 'none' }} />
                    </button>
                  </div>
                </td>
              </tr>
            ));

          const renderAssignedTable = (list, label, nameColHeader) => (
            <div style={{ marginBottom: '24px' }}>
              <h6
                style={{
                  fontWeight: 600,
                  marginBottom: '12px',
                  padding: '10px 14px',
                  borderLeft: '4px solid #4361ee',
                  borderRadius: '6px',
                  background: '#f8fafc',
                }}
              >
                {label}
              </h6>
              <div style={{ overflowX: 'auto' }}>
              {/* Bootstrap paints every cell from --bs-table-bg, so set the grey there. */}
              <table
                className="table table-bordered mb-0"
                style={{
                  marginTop: '4px',
                  '--bs-table-bg': 'hsl(var(--card))',
                  '--bs-table-border-color': '#cbd5e1',
                } as React.CSSProperties}
              >
                <thead style={{ '--bs-table-bg': 'hsl(var(--muted))' } as React.CSSProperties}>
                  <tr>
                    <th style={tableCellStyle}>S.No</th>
                    <th style={tableCellStyle}>{nameColHeader}</th>
                    <th style={tableCellStyle}>Mobile Number</th>
                    <th style={tableCellStyle}>Agent Status</th>
                    <th style={tableCellStyle}>Start Time</th>
                    <th style={tableCellStyle}>End Time</th>
                    <th style={tableCellStyle}>Priority</th>
                    <th style={tableCellStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.length > 0 ? (
                    renderAssignedRows(list)
                  ) : (
                    <tr>
                      <td colSpan={8} className="text-center" style={{ padding: '16px' }}>
                        No agents found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          );

          return (
            <>
              {renderAssignedTable(teamLeadAssigned, 'Team Lead Agents', 'Team Member Name')}
              {renderAssignedTable(regularAssigned, 'Regular Agents', 'Agent Name')}
            </>
          );
        })()}
        {hasUnsavedPriorityChanges && (
          <div className="d-flex flex-column align-items-end" style={{ marginTop: '4px' }}>
            {Object.keys(priorityRowErrors).length > 0 && (
              <p style={{ color: '#dc3545', fontSize: '13px', marginBottom: '8px' }}>
                Fix the highlighted priorities before saving.
              </p>
            )}
            <button
              type="button"
              className="btn btn-primary px-4"
              disabled={savingPriorities}
              onClick={saveAssignedPriorities}
            >
              {savingPriorities ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        )}
      </div>

      {/* Add Members Modal */}
      <Modal show={showAddMembersModal} onHide={handleModalClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Members in Call Group</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px 24px' }}>
          <Row>
            <Col>
              {(() => {
                const regularAgents = availableAgents.filter((a) => a.access === 'regular');
                const teamLeadAgents = availableAgents.filter((a) => a.access === 'team_lead');

                const renderAgentRows = (agentList) =>
                  agentList.map((agent, index) => (
                    <tr key={agent.id}>
                      <td style={{ padding: '10px 14px' }}>{index + 1}</td>
                      <td style={{ padding: '10px 14px' }}>{agent.agent_name}</td>
                      <td style={{ padding: '10px 14px' }}>{agent.agent_number}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ padding: '4px' }}>
                          <input
                            type="number"
                            min={0}
                            max={10}
                            placeholder="Default:10"
                            value={agentPriorities[String(agent.id)] ?? ''}
                            style={{
                              width: '100px',
                              display: 'block',
                              padding: '6px 10px',
                              border: addMemberPriorityErrors[agent.id] ? '1px solid red' : '1px solid #ced4da',
                              borderRadius: '4px',
                              backgroundColor: addMemberPriorityErrors[agent.id] ? '#fff5f5' : '#fff',
                              outline: 'none',
                            }}
                            onChange={(e) => {
                              const val = e.target.value;
                              setAgentPriorities((prev) => {
                                const updated = { ...prev, [String(agent.id)]: val };
                                const newErrors: any = {};
                                selectedAgentIds.forEach((id) => {
                                  const v = updated[String(id)] ?? '10';
                                  const n = parseInt(v, 10);
                                  if (v === '' || isNaN(n) || n < 0 || n > 10) {
                                    newErrors[String(id)] = true;
                                  }
                                });
                                setAddMemberPriorityErrors(newErrors);
                                return updated;
                              });
                            }}
                          />
                          {addMemberPriorityErrors[agent.id] && (
                            <div style={{ color: 'red', fontSize: '10px', marginTop: '2px' }}>
                              0-10 required
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <input
                          type="checkbox"
                          checked={selectedAgentIds.includes(agent.id)}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedAgentIds((prev) => [...prev, agent.id]);
                            else setSelectedAgentIds((prev) => prev.filter((id) => id !== agent.id));
                          }}
                        />
                      </td>
                    </tr>
                  ));

                const renderTable = (agentList, label, allSelected, onSelectAll) => (
                  <div style={{ marginBottom: '20px' }}>
                    <h6
                      style={{
                        fontWeight: 600,
                        marginBottom: '8px',
                        padding: '6px 10px',
                        borderLeft: '4px solid #4361ee',
                        borderRadius: '4px',
                      }}
                    >
                      {label}
                    </h6>
                    <div style={{ maxHeight: '260px', overflowY: 'auto', scrollbarWidth: 'auto' }}>
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th style={{ padding: '10px 14px' }}>S.No</th>
                            <th style={{ padding: '10px 14px' }}>Team Member Name</th>
                            <th style={{ padding: '10px 14px' }}>Mobile Number</th>
                            <th style={{ padding: '10px 14px' }}>Priority (0-10)</th>
                            <th style={{ padding: '10px 14px' }}>
                              <input type="checkbox" onChange={onSelectAll} checked={allSelected} />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {agentList.length === 0 ? (
                            <tr>
                              <td colSpan={5} style={{ textAlign: 'center', color: '#888' }}>
                                No agents available
                              </td>
                            </tr>
                          ) : (
                            renderAgentRows(agentList)
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );

                return (
                  <>
                    {renderTable(
                      regularAgents,
                      'Regular Agents',
                      regularAgents.length > 0 && regularAgents.every((a) => selectedAgentIds.includes(a.id)),
                      (e) => {
                        if (e.target.checked) {
                          setSelectedAgentIds((prev) => [...new Set([...prev, ...regularAgents.map((a) => a.id)])]);
                        } else {
                          const ids = new Set(regularAgents.map((a) => a.id));
                          setSelectedAgentIds((prev) => prev.filter((id) => !ids.has(id)));
                        }
                      },
                    )}
                    {renderTable(
                      teamLeadAgents,
                      'Team Lead Agents',
                      teamLeadAgents.length > 0 && teamLeadAgents.every((a) => selectedAgentIds.includes(a.id)),
                      (e) => {
                        if (e.target.checked) {
                          setSelectedAgentIds((prev) => [...new Set([...prev, ...teamLeadAgents.map((a) => a.id)])]);
                        } else {
                          const ids = new Set(teamLeadAgents.map((a) => a.id));
                          setSelectedAgentIds((prev) => prev.filter((id) => !ids.has(id)));
                        }
                      },
                    )}
                  </>
                );
              })()}
            </Col>
          </Row>

          {isGroupScheduled ? (
            <Row>
              <Col>
                <Form>
                  <Form.Check
                    type="checkbox"
                    label="Whole Day"
                    id="wholeDay"
                    className="mb-3"
                    checked={wholeDay}
                    onChange={handleWholeDayChange}
                    disabled={!(timeScheduledDate?.startTime === '00:00' && timeScheduledDate?.endTime === '23:59')}
                  />
                  <Form.Group className="mb-3">
                    <Form.Label>Start Time</Form.Label>
                    <CalendarTimeInput
                      value={startTime}
                      onChange={(v) => handleStartTimeChange({ target: { value: v } } as any)}
                      min={timeScheduledDate?.startTime}
                      max={timeScheduledDate?.endTime}
                      placeholder="00:00:00 AM"
                      disabled={wholeDay}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>End Time</Form.Label>
                    <CalendarTimeInput
                      value={endTime}
                      onChange={(v) => handleEndTimeChange({ target: { value: v } } as any)}
                      min={timeScheduledDate?.startTime}
                      max={timeScheduledDate?.endTime}
                      placeholder="11:59:59 PM"
                      disabled={wholeDay}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Whole Week"
                      id="memberWholeWeek"
                      className="mb-3"
                      checked={hasSelectedWholeWeek(memberWeekDays, timeScheduledDate?.weekDays)}
                      onChange={(e) => {
                        setMemberWholeWeek(e.target.checked);
                        setmemberWeekDays(e.target.checked ? getWholeWeekDays(timeScheduledDate?.weekDays) : []);
                      }}
                    />
                    <Form.Label>Select Days</Form.Label>
                    <div>
                      {timeScheduledDate?.weekDays.map((day) => (
                        <Form.Check
                          inline
                          key={day}
                          label={day}
                          type="checkbox"
                          checked={memberWeekDays.includes(day)}
                          disabled={memberWholeWeek}
                          onChange={(e) => {
                            if (e.target.checked) setmemberWeekDays((prev) => [...prev, day]);
                            else setmemberWeekDays((prev) => prev.filter((d) => d !== day));
                          }}
                        />
                      ))}
                    </div>
                  </Form.Group>
                </Form>
              </Col>
              <div
                style={{
                  background: '#FFF8C6',
                  color: '#8B5A2B',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                <span>
                  <strong>Note: </strong>Member's Time Schedule must fall within the Range of Call Group Time Schedule.
                </span>
              </div>
            </Row>
          ) : (
            <div
              style={{
                background: '#FFF8C6',
                color: '#8B5A2B',
                padding: '8px 12px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              <span>
                <strong>Note: </strong>
                {notes.find((n) => Number(n.priority) === 1)?.message ?? ''}
              </span>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handleAddMembersSave} disabled={!isGroupScheduled || savingMembers}>
            {savingMembers ? 'Adding...' : 'Add member'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Call strategy */}
      <Modal show={showCallStrategyModal} onHide={handleCallStrategyCloseall} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>Select Call Strategy</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Select a Strategy:</Form.Label>
              <div>
                {callStrategy.map((strategy) => (
                  <Form.Check
                    style={{ marginTop: '5px' }}
                    type="radio"
                    key={strategy.name}
                    label={strategy.name}
                    name="callStrategy"
                    id={strategy.name.replace(/\s+/g, '-').toLowerCase()}
                    checked={selectedStrategy === strategy.name}
                    onChange={() => setSelectedStrategy(strategy.name)}
                  />
                ))}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCallStrategyCloseall}>
            Close
          </Button>
          <Button variant="primary" onClick={handleCallStrategyClose} disabled={savingStrategy}>
            {savingStrategy ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Time Schedule Modal */}
      <Modal show={showTimeScheduleModal} onHide={handleTimeScheduleClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Time Schedule Setting</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Check
              type="checkbox"
              label="Whole Day"
              id="wholeDay"
              className="mb-3"
              checked={timeScheduleWholeDay}
              onChange={(e) => {
                setTimeScheduleWholeDay(e.target.checked);
                setTimeScheduledDate((prev) => ({
                  ...prev,
                  startTime: e.target.checked ? DEFAULT_SCHEDULE_START : '',
                  endTime: e.target.checked ? DEFAULT_SCHEDULE_END : '',
                }));
              }}
            />
            <Form.Group className="mb-3">
              <Form.Label>Start Time</Form.Label>
              <CalendarTimeInput
                value={timeScheduledDate.startTime}
                onChange={(e) => setTimeScheduledDate((prev) => ({ ...prev, startTime: e }))}
                placeholder="00:00"
                disabled={timeScheduleWholeDay}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>End Time</Form.Label>
              <CalendarTimeInput
                value={timeScheduledDate.endTime}
                onChange={(e) => setTimeScheduledDate((prev) => ({ ...prev, endTime: e }))}
                placeholder="23:59"
                disabled={timeScheduleWholeDay}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Whole Week"
                id="groupScheduleWholeWeek"
                className="mb-3"
                checked={hasSelectedWholeWeek(timeScheduledDate?.weekDays, weekDays)}
                onChange={(e) => {
                  setTimeScheduleWholeWeek(e.target.checked);
                  setTimeScheduledDate((prev) => ({
                    ...prev,
                    weekDays: e.target.checked ? getWholeWeekDays(weekDays) : [],
                  }));
                }}
              />
              <Form.Label>Select all Days</Form.Label>
              <div>
                {weekDays.map((day) => (
                  <Form.Check
                    inline
                    key={day}
                    label={day}
                    type="checkbox"
                    checked={timeScheduledDate?.weekDays?.includes(day)}
                    disabled={timeScheduleWholeWeek}
                    onChange={(e) => {
                      setTimeScheduledDate((prev) => ({
                        ...prev,
                        weekDays: e.target.checked ? [...prev.weekDays, day] : prev.weekDays.filter((d) => d !== day),
                      }));
                    }}
                  />
                ))}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleTimeScheduleClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSaveTimeSchedule} disabled={savingSchedule}>
            {savingSchedule ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* No Agent Prompt */}
      <Modal show={showNoAgentPromptModal} onHide={() => setShowNoAgentPromptModal(false)} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>No Agent Available Prompt</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Please select no agent prompt</Form.Label>
              <Form.Control
                as="select"
                name="noAgentPrompt"
                value={selectedNoAgentPromptId}
                onChange={(e) => {
                  setSelectedNoAgentPromptId(e.target.value);
                  setSelectedNoAgentPromptLanguage('');
                }}
                disabled={loadingNoAgentPromptOptions}
              >
                <option value="">{loadingNoAgentPromptOptions ? 'Loading prompts...' : '-- Not selected --'}</option>
                {noAgentPromptOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.prompt_name}
                  </option>
                ))}
              </Form.Control>
              <small className="text-muted d-block mt-2">
                This is optional. You can submit with or without selecting a prompt.
              </small>
            </Form.Group>
            {selectedNoAgentPromptId && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Prompt Language <span style={{ color: 'red' }}>*</span>
                </Form.Label>
                <Form.Control
                  as="select"
                  name="noAgentPromptLanguage"
                  value={selectedNoAgentPromptLanguage}
                  onChange={(e) => setSelectedNoAgentPromptLanguage(e.target.value)}
                  disabled={noAgentPromptLanguages.length === 0}
                >
                  <option value="">
                    {noAgentPromptLanguages.length === 0 ? 'No approved language for this prompt' : 'Select Language'}
                  </option>
                  {noAgentPromptLanguages.map((entry) => (
                    <option key={entry.language} value={entry.language}>
                      {String(entry.language).toUpperCase()}
                    </option>
                  ))}
                </Form.Control>
                <small className="text-muted d-block mt-2">
                  This prompt has {noAgentPromptLanguages.length} approved language
                  {noAgentPromptLanguages.length === 1 ? '' : 's'}. Pick the one to play.
                </small>
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleNoAgentPromptSave} disabled={savingNoAgentPrompt}>
            {savingNoAgentPrompt ? 'Saving...' : 'Submit'}
          </Button>
          <Button variant="secondary" onClick={() => setShowNoAgentPromptModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Customer Disclaimer */}
      <Modal
        show={showCustomerDisclaimerModal}
        onHide={() => {
          setShowCustomerDisclaimerModal(false);
          getAgentGroupData();
        }}
        size="md"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Customer Disclaimer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                Select Prompt <span style={{ color: 'red' }}>*</span>
              </Form.Label>
              <Form.Control
                as="select"
                name="customerDisclaimerPrompt"
                value={selectedCustomerDisclaimerPromptId}
                onChange={(e) => {
                  setSelectedCustomerDisclaimerPromptId(e.target.value);
                  setSelectedCustomerDisclaimerLanguage('');
                }}
                disabled={loadingNoAgentPromptOptions}
              >
                <option value="">{loadingNoAgentPromptOptions ? 'Loading prompts...' : '-- Not selected --'}</option>
                {approvedPromptOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.prompt_name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            {selectedCustomerDisclaimerPromptId && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Prompt Language <span style={{ color: 'red' }}>*</span>
                </Form.Label>
                <Form.Control
                  as="select"
                  name="customerDisclaimerLanguage"
                  value={selectedCustomerDisclaimerLanguage}
                  onChange={(e) => setSelectedCustomerDisclaimerLanguage(e.target.value)}
                  disabled={customerDisclaimerLanguages.length === 0}
                >
                  <option value="">
                    {customerDisclaimerLanguages.length === 0
                      ? 'No approved language for this prompt'
                      : 'Select Language'}
                  </option>
                  {customerDisclaimerLanguages.map((entry) => (
                    <option key={entry.language} value={entry.language}>
                      {String(entry.language).toUpperCase()}
                    </option>
                  ))}
                </Form.Control>
                <small className="text-muted d-block mt-2">
                  This prompt has {customerDisclaimerLanguages.length} approved language
                  {customerDisclaimerLanguages.length === 1 ? '' : 's'}. Pick the one to play.
                </small>
              </Form.Group>
            )}
            <small className="text-muted d-block">
              Both prompt and language are required to keep the disclaimer on.
            </small>
            <small className="text-muted d-block mt-2">
              Note: The disclaimer keeps playing to the customer only while the call has not entered a call group. Once the
              call enters a call group, the call group&apos;s own settings take over immediately.
            </small>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCustomerDisclaimerSave} disabled={savingCustomerDisclaimer}>
            {savingCustomerDisclaimer ? 'Saving...' : 'Submit'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setShowCustomerDisclaimerModal(false);
              getAgentGroupData();
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Whisper */}
      <Modal show={showWhisperModal} onHide={() => setShowWhisperModal(false)} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>Agent Whisper Prompt</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Select Prompt</Form.Label>
              <Form.Control
                as="select"
                name="audio"
                value={selectedWhisper}
                onChange={(e) => {
                  setSelectedWhisper(e.target.value);
                  setSelectedWhisperLanguage('');
                }}
              >
                <option value="">Select Prompt</option>
                {audio.map((actionKey) => (
                  <option key={actionKey.id} value={actionKey.id}>
                    {actionKey.prompt_name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            {selectedWhisper && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Prompt Language <span style={{ color: 'red' }}>*</span>
                </Form.Label>
                <Form.Control
                  as="select"
                  name="whisperLanguage"
                  value={selectedWhisperLanguage}
                  onChange={(e) => setSelectedWhisperLanguage(e.target.value)}
                  disabled={whisperPromptLanguages.length === 0}
                >
                  <option value="">
                    {whisperPromptLanguages.length === 0 ? 'No approved language for this prompt' : 'Select Language'}
                  </option>
                  {whisperPromptLanguages.map((entry) => (
                    <option key={entry.language} value={entry.language}>
                      {String(entry.language).toUpperCase()}
                    </option>
                  ))}
                </Form.Control>
                <small className="text-muted d-block mt-2">
                  This prompt has {whisperPromptLanguages.length} approved language
                  {whisperPromptLanguages.length === 1 ? '' : 's'}. Pick the one to play.
                </small>
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleWhisperSave} disabled={savingWhisper}>
            {savingWhisper ? 'Saving...' : 'Submit'}
          </Button>
          <Button variant="secondary" onClick={handleWhisperClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Music on Hold */}
      <Modal show={showMusicOnHold} onHide={() => setShowMusicOnHold(false)} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>Music On Hold</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Select MOH File</Form.Label>
              <Form.Control
                as="select"
                name="moh"
                value={selectedMusicHold ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedMusicHold(val === '' ? null : val);
                }}
                disabled={mohLoading}
              >
                <option value="">
                  {mohLoading ? 'Loading MOH list...' : mohOptions.length === 0 ? 'No approved MOH available' : '-- None --'}
                </option>
                {mohOptions.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.moh_name}
                  </option>
                ))}
              </Form.Control>
              {mohError && <small className="text-danger d-block mt-1">{mohError}</small>}
              <div className="d-flex gap-2 mt-2">
                <Button size="sm" variant="outline-secondary" onClick={fetchMohOptions} disabled={mohLoading}>
                  {mohLoading ? 'Refreshing...' : 'Refresh List'}
                </Button>
                {selectedMusicHold && (
                  <Button size="sm" variant="outline-danger" onClick={() => setSelectedMusicHold(null)}>
                    Clear Selection
                  </Button>
                )}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleMusicHoldSave} disabled={mohLoading}>
            Submit
          </Button>
          <Button variant="secondary" onClick={() => setShowMusicOnHold(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Call Script Modal */}
      <Modal show={showCallScriptModal} onHide={handleCallScriptClose} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>Select Call Script</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                Call Script <span style={{ color: '#6c757d', fontSize: '12px' }}>(optional)</span>
              </Form.Label>
              <Form.Control
                as="select"
                name="callScript"
                value={pendingCallScriptId}
                disabled={loadingCallScripts || savingCallScript}
                onChange={(e) => setPendingCallScriptId(e.target.value)}
              >
                <option value="">{loadingCallScripts ? 'Loading scripts...' : '-- None (remove script) --'}</option>
                {callScripts.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.title || `Script #${s.id}`}
                  </option>
                ))}
              </Form.Control>
              {!loadingCallScripts && callScripts.length === 0 && (
                <Form.Text className="text-muted">
                  No call scripts found. Create a script first from the Call Scripts page.
                </Form.Text>
              )}
            </Form.Group>

            {pendingCallScriptId
              ? (() => {
                  const preview = callScripts.find((s) => String(s.id) === String(pendingCallScriptId));
                  if (!preview) return null;
                  return (
                    <div style={{ border: '1px solid #e2e8f021', borderRadius: '6px', padding: '10px 12px', fontSize: '13px' }}>
                      <div className="mb-1">
                        <strong>Title: </strong>
                        {preview.title || '-'}
                      </div>
                      {preview.short_description ? (
                        <div className="mb-1">
                          <strong>Summary: </strong>
                          {preview.short_description}
                        </div>
                      ) : null}
                    </div>
                  );
                })()
              : null}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          {selectedCallScriptId ? (
            <Button variant="outline-danger" onClick={handleCallScriptRemove} disabled={savingCallScript}>
              {savingCallScript ? 'Removing...' : 'Remove Script'}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={handleCallScriptClose} disabled={savingCallScript}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleCallScriptSave}
            disabled={
              savingCallScript ||
              loadingCallScripts ||
              String(pendingCallScriptId || '') === String(selectedCallScriptId || '')
            }
          >
            {savingCallScript ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Disposition Modal */}
      <Modal show={showDispositionModal} onHide={handleDispositionClose} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Select {dispositionModalFlow === 'outbound' ? 'Outbound' : 'Inbound'} Disposition Form
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(() => {
            const filteredForms = dispositionForms.filter(
              (d) => formMatchesCallDirection(d.call_direction, dispositionModalFlow),
            );
            return (
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {dispositionModalFlow === 'outbound' ? 'Outbound' : 'Inbound'} Disposition Form{' '}
                    <span style={{ color: '#6c757d', fontSize: '12px' }}>(optional)</span>
                  </Form.Label>
                  <Form.Control
                    as="select"
                    name="dispositionForm"
                    value={pendingDispositionId}
                    disabled={loadingDispositions || savingDisposition}
                    onChange={(e) => setPendingDispositionId(e.target.value)}
                  >
                    <option value="">
                      {loadingDispositions ? 'Loading disposition forms...' : '-- None (remove disposition) --'}
                    </option>
                    {filteredForms.map((d) => (
                      <option key={d.id} value={String(d.id)}>
                        {d.name || `Form #${d.id}`}
                        {d.is_default ? ' (Default)' : ''}
                        {String(d.call_direction).toLowerCase() === 'both' ? ' (Both)' : ''}
                      </option>
                    ))}
                  </Form.Control>
                  {!loadingDispositions && filteredForms.length === 0 && (
                    <Form.Text className="text-muted">
                      No matching {dispositionModalFlow} or Both disposition forms found. Create one first from the Disposition Forms page.
                    </Form.Text>
                  )}
                </Form.Group>

                {pendingDispositionId
                  ? (() => {
                      const preview = dispositionForms.find((d) => String(d.id) === String(pendingDispositionId));
                      if (!preview) return null;
                      const fieldCount = Array.isArray(preview.fields) ? preview.fields.length : 0;
                      return (
                        <div style={{ border: '1px solid #e2e8f021', borderRadius: '6px', padding: '10px 12px', fontSize: '13px' }}>
                          <div className="mb-1">
                            <strong>Name: </strong>
                            {preview.name || '-'}
                            {preview.is_default ? <span className="badge bg-success ms-2">Default</span> : null}
                          </div>
                          <div className="mb-1">
                            <strong>Call flow: </strong>
                            {formatDispositionCallDirectionLabel(preview.call_direction)}
                          </div>
                          <div className="mb-1">
                            <strong>Fields: </strong>
                            {fieldCount}
                          </div>
                        </div>
                      );
                    })()
                  : null}
              </Form>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          {dispositionFlowConfig[dispositionModalFlow]?.currentValue ? (
            <Button variant="outline-danger" onClick={() => handleDispositionRemove()} disabled={savingDisposition}>
              {savingDisposition ? 'Removing...' : 'Remove Disposition'}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={handleDispositionClose} disabled={savingDisposition}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleDispositionSave}
            disabled={
              savingDisposition ||
              loadingDispositions ||
              String(pendingDispositionId || '') === String(dispositionFlowConfig[dispositionModalFlow]?.currentValue || '')
            }
          >
            {savingDisposition ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Member Schedule */}
      <Modal
        show={showEditModal}
        onHide={() => {
          if (ignoreEditModalCloseRef.current) return;
          setShowEditModal(false);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Edit Member{editData?.agent_name ? ` — ${editData.agent_name}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Check
              type="checkbox"
              label="Whole Day"
              id="wholeDay"
              className="mb-3"
              checked={editDataWholeDay}
              disabled={!(timeScheduledDate?.startTime === '00:00' && timeScheduledDate?.endTime === '23:59')}
              onChange={(e) => {
                setEditDataWholeDay(e.target.checked);
                setEditData((prev) => ({
                  ...prev,
                  startTime: e.target.checked ? '00:00' : '',
                  endTime: e.target.checked ? '23:59' : '',
                }));
              }}
            />

            <Form.Group className="mb-3">
              <Form.Label>Start Time</Form.Label>
              <CalendarTimeInput
                value={editData.startTime}
                onChange={(e) => {
                  const value = e;
                  if (value < timeScheduledDate.startTime || value > timeScheduledDate.endTime) {
                    toast.error(
                      `Start time should be within allowed Range (${convertTo12Hour(timeScheduledDate.startTime)} to ${convertTo12Hour(timeScheduledDate.endTime)})`,
                    );
                    return;
                  }
                  setEditData((prev) => ({ ...prev, startTime: e }));
                }}
                min={timeScheduledDate?.startTime}
                max={timeScheduledDate?.endTime}
                placeholder="00:00"
                disabled={editDataWholeDay}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>End Time</Form.Label>
              <CalendarTimeInput
                value={editData.endTime}
                onChange={(e) => {
                  const value = e;
                  if (value < timeScheduledDate.startTime || value > timeScheduledDate.endTime) {
                    toast.error(
                      `End time should be within allowed range (${convertTo12Hour(editData.startTime)} to ${convertTo12Hour(timeScheduledDate.endTime)})`,
                    );
                    return;
                  }
                  if (value < editData.startTime) {
                    toast.error('End time cannot be less than start time');
                    return;
                  }
                  setEditData((prev) => ({ ...prev, endTime: e }));
                }}
                min={timeScheduledDate?.startTime}
                max={timeScheduledDate?.endTime}
                placeholder="23:59"
                disabled={editDataWholeDay}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Whole Week"
                id="editMemberWholeWeek"
                className="mb-3"
                checked={hasSelectedWholeWeek(editData?.weekDays, timeScheduledDate?.weekDays)}
                onChange={(e) => {
                  setEditData((prev) => ({
                    ...prev,
                    weekDays: e.target.checked ? getWholeWeekDays(timeScheduledDate?.weekDays) : [],
                  }));
                }}
              />
              <Form.Label>Select Days</Form.Label>
              <div>
                {(Array.isArray(timeScheduledDate?.weekDays) ? timeScheduledDate.weekDays : []).map((day) => (
                  <Form.Check
                    inline
                    key={day}
                    label={day}
                    type="checkbox"
                    checked={Array.isArray(editData?.weekDays) && editData.weekDays.includes(day)}
                    onChange={(e) => {
                      setEditData((prev) => {
                        const days = Array.isArray(prev.weekDays) ? prev.weekDays : [];
                        return {
                          ...prev,
                          weekDays: e.target.checked ? [...days, day] : days.filter((d) => d !== day),
                        };
                      });
                    }}
                  />
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Priority <span style={{ color: 'red', margin: '0 4px' }}>*</span>
              </Form.Label>
              <Form.Control
                type="number"
                name="priority"
                min={0}
                max={10}
                required
                value={editData.priority ?? ''}
                placeholder="Enter priority (0-10)"
                style={{
                  borderColor: priorityError ? '#dc3545' : undefined,
                  backgroundColor: priorityError ? '#fff5f5' : undefined,
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  const numValue = parseInt(value, 10);
                  if (value === '') {
                    setPriorityError('Priority is required and must be a number between 0 and 10');
                    setEditData((prev) => ({ ...prev, priority: value }));
                  } else if (isNaN(numValue) || numValue < 0 || numValue > 10) {
                    setPriorityError('Priority must be a number between 0 and 10');
                    setEditData((prev) => ({ ...prev, priority: value }));
                  } else {
                    setPriorityError('');
                    setEditData((prev) => ({ ...prev, priority: value }));
                  }
                }}
              />
              <Form.Text className="text-muted" style={{ fontSize: '0.8rem' }}>
                This priority applies only to this member.
              </Form.Text>
              {priorityError && (
                <Form.Text className="text-danger d-block" style={{ fontSize: '0.875rem' }}>
                  {priorityError}
                </Form.Text>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowEditModal(false)}
          >
            Close
          </Button>
          <Button variant="primary" onClick={handleSaveAgentEdit} disabled={savingAgentSchedule}>
            {savingAgentSchedule ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
        <div
          style={{
            background: '#FFF8C6',
            color: '#8B5A2B',
            padding: '8px 12px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            fontSize: '14px',
            fontWeight: 500,
            margin: '10px',
          }}
        >
          <span>
            <strong>Note: </strong>Member's Time Schedule must fall within the Range of Call Group Time Schedule.
          </span>
        </div>
      </Modal>
    </ScrollPage>
  );
};

export default GroupSettings;
